/**
 * Pedidos (DEMO-03, telas/Pedidos.tsx), persistentes (B4). O Pedido é gerado
 * AUTOMATICAMENTE no ganho (PED-), a partir da VERSÃO ACEITA e só das
 * quantidades GANHAS — sem redigitação. Cliente, entidade, faturamento,
 * entrega, itens, custos, preços, margens, frete e condições ficam congelados
 * (o banco recusa alterações). OC do Cliente é referência (documento recebido),
 * vinculada no resultado ou depois; substituir é 🔒 com justificativa.
 * Ordens (B5): conferência antes de gerar as OCF (uma por entidade faturadora,
 * alocação do frete fechando exatamente); OL/OTI para Locação e Trade-in;
 * situação do Pedido tipo Venda calculada pelas Ordens válidas.
 */
import { useState, type ReactNode } from 'react';
import { navegar } from '../rotas.ts';
import { api, consulta } from '../api.ts';
import { useSessao } from '../sessao.tsx';
import { useRotuloTrilha } from '../componentes/trilha.tsx';
import { buscar, dataBr, noPeriodo, periodoInicial, textoMotivo, TIPOS, type Chave, type Tipo } from '../../../backend/src/compartilhado/oportunidades.ts';
import { economia, liquidaEstimada, pagamentoTexto, PAGADORES, recebimentoTradein, regraComissao, type LocalCongelado } from '../../../backend/src/compartilhado/propostas.ts';
import { textoReajuste } from '../../../backend/src/compartilhado/comercial.ts';
import { TIPOS_OC, NOMES_TIPO_OC, type ConteudoPedido, type TipoOc } from '../../../backend/src/compartilhado/pedidos.ts';
import { conferirAlocacao, ESTADOS_ORDEM, NOME_DOCUMENTO, type Alocacao, type EstadoOrdem } from '../../../backend/src/compartilhado/ordens.ts';
import { margem as margemDe, percentual as percentualDe, totalizar, type LinhaConciliada, type Valores } from '../../../backend/src/compartilhado/conciliacao.ts';
import { SeloOrdem, tomOrdem } from './Ordens.tsx';
import { RegrasRecebimentoDoPedido } from './RegraRecebimento.tsx';
import { Ajuda, CardLista, CardsLista, ChipsStatus, CampoBusca, Estado, EstadoCarga, Modal, ResumoIndicadores, RolagemIndicada, SeloSituacao, Th, useCarga, useOrdenacao, type Tom } from './ui.tsx';
import { Icone } from '../componentes/icones.tsx';
import { usePersonalizacao } from './personalizacao.tsx';
import { BarraFiltros, FiltroMultiplo, FiltroPeriodo, filtroSelecao, filtroVariosComAplicar, SeloTipo, usePeriodo } from './filtros.tsx';
import { CampoData, hojeIso } from './CampoData.tsx';
import { moeda, moeda2, pct, pctNum } from './proposta/formatos.ts';
import { BotaoExcluir, depoisDeExcluir } from './Lixeira.tsx';
import { ModalCancelarPedido, ModalReaberturaComercial, ModalReabrirCorrecao, RetificacoesDoPedido, type RetificacaoAplicada } from './Retificacao.tsx';

export type SituacaoPedido = 'Em andamento' | 'Parcialmente cancelado' | 'Faturado parcialmente' | 'Faturado parcialmente · saldo cancelado' | 'Faturado' | 'Cancelado';
/** P4 bloco 3 (I07): ordem de apresentação aprovada; o cálculo do Status não muda. */
const SITUACOES_PED: SituacaoPedido[] = ['Em andamento', 'Faturado', 'Faturado parcialmente', 'Faturado parcialmente · saldo cancelado', 'Parcialmente cancelado', 'Cancelado'];
/** P2-15: opções do filtro múltiplo de Status (as 5 regras da C12), com identificador estável para teste. */
const SLUG_PED: Record<SituacaoPedido, string> = { 'Em andamento': 'em_andamento', 'Parcialmente cancelado': 'parcialmente_cancelado', 'Faturado parcialmente': 'faturado_parcialmente', 'Faturado parcialmente · saldo cancelado': 'saldo_cancelado', Faturado: 'faturado', Cancelado: 'cancelado' };
const OPCOES_STATUS_PED = SITUACOES_PED.map((s) => ({ id: SLUG_PED[s], nome: s }));
const TODOS_STATUS_PED = OPCOES_STATUS_PED.map((o) => o.id);
/** Selo da situação do Pedido (C12): a cor nunca é o único meio — o texto vem inteiro. */
export const tomPedido = (s: SituacaoPedido): Tom => (s === 'Faturado' ? 'positivo' : s === 'Cancelado' ? 'encerrado' : s === 'Em andamento' ? 'aberto' : 'parcial');

export interface OcCliente {
  id: string;
  numero: string;
  tipo: string;
  dataRecebimento: string | null;
  observacao: string;
  arquivo: { nome: string; tamanho: number } | null;
  situacao: 'Vigente' | 'Substituída';
  substituiId: string | null;
  substituidaPor: string | null;
  motivoSubstituicao: string | null;
  usuario: string;
  registradaEm: string;
  versao: number;
  itens: string[];
  /** Pacote 4: 'pedido' | 'itens' declarado; null = registro anterior (vínculo gravado mostrado como está) */
  abrangencia?: 'pedido' | 'itens' | null;
}
export interface PedidoResumo {
  id: string;
  numero: string;
  tipo: Tipo;
  cliente: { id: string; nome: string };
  oportunidade: { id: string; numero: string; titulo: string; responsavel: string };
  proposta: { id: string; numero: string };
  versao: number;
  geradoEm: string;
  geradoPor: string;
  tipoCopiadoEm: string;
  dataFechamento: string;
  faturamento: LocalCongelado;
  /** saldoCancelado: R$ não faturado das Ordens canceladas (D2) */
  valores: { vendido: number; custo: number; margem: number; percentual: number; aFaturar: number; saldoCancelado?: number };
  /** P3-13 a P3-17: revisão pela retificação e cancelamento sem Ordem */
  revisao?: number;
  cancelamento?: { em: string; motivo: string; observacao: string } | null;
  /** P3-33 a P3-36 (D4): linhas conciliadas no corte de hoje */
  conciliacao?: LinhaConciliada[];
  parceiros: { id: string; nome: string }[];
  linhas: { quantidade: number; custoUnit: number; vendaUnit: number; grupoId: string }[];
  situacao: SituacaoPedido;
  ordens: { numero: string; tipo: 'venda' | 'locacao' | 'trade_in'; situacao: string; estado: string }[];
  ocs: OcCliente[];
  ocsVigentes: string[];
  ocPendente: boolean;
  /** Pacote 4 (bloco 2): sem OC vigente, mas com a declaração "Não se aplica" ativa (não é pendência) */
  ocNaoSeAplica?: boolean;
  /** Pacote 4 (bloco 2): base própria da Locação (parcela do Cliente e periodicidade) */
  locacao?: { parcela: number; periodicidade: string } | null;
  chaves: Chave[];
}
export interface PedidoCompleto extends PedidoResumo {
  conteudo: ConteudoPedido;
  retificacoes?: RetificacaoAplicada[];
  pendenciasAbertas?: number;
  entidades: Record<string, { razao: string; fantasia: string; cnpj: string; grupoId: string; grupo: string } | null>;
  clienteCnpj: string;
  linhasVersao: number;
  naoGanhos: string[];
  ordensDetalhe: { numero: string; estado: EstadoOrdem; entidade: { razao: string; fantasia: string; cnpj: string; grupoId: string; grupo: string } | null; dataCriacao: string; enviada: boolean; enviadaEm?: string | null; itens?: string[]; custo: number; frete: number; progresso: { total: number; faturada: number }; substituta: string | null; origem: string | null }[];
  documentosDetalhe: { numero: string; tipo: 'locacao' | 'trade_in'; situacao: string; dataCriacao: string }[];
  emOrdens: Record<string, number>;
  faturadoPorItem: Record<string, number>;
  freteAlocado: number;
  freteAAlocar: number;
  previaOrdens: { entidadeId: string; entidade: { razao: string; fantasia: string; cnpj: string; grupoId: string; grupo: string } | null; itens: { itemId: string; quantidade: number }[] }[];
  alocacaoSugerida: Alocacao;
  /** Pacote 4: declarações "Não se aplica" da OC do Cliente (ativa e encerradas) */
  ocDeclaracoes?: { id: string; justificativa: string; em: string; usuario: string; encerradaEm: string | null; encerradaPor: string | null }[];
  /** Pacote 4 (bloco 2): série do documento operacional configurada neste ambiente (null na Venda) */
  serieDocumento?: { serie: 'ol' | 'oti'; configurada: boolean } | null;
}

type DataPed = 'criacao' | 'fechamento';
type ColPed = 'numero' | 'cliente' | 'tipo' | 'proposta' | 'parceiros' | 'criacao' | 'fechamento' | 'vendido' | 'custo' | 'margem' | 'margemPct' | 'situacao' | 'ordens';
const DATAS_PED: { id: DataPed; rotulo: string }[] = [
  { id: 'criacao', rotulo: 'Data de criação do Pedido' },
  { id: 'fechamento', rotulo: 'Data de fechamento do Pedido' },
];

/** Resumo das Ordens do Pedido na lista: quantas por situação (OCF) e cada OL/OTI com a sua situação. */
const resumoOrdens = (p: Pick<PedidoResumo, 'ordens'>) => {
  const ocf = p.ordens.filter((o) => o.tipo === 'venda');
  const por = (Object.keys(ESTADOS_ORDEM) as EstadoOrdem[]).map((e) => [ESTADOS_ORDEM[e], ocf.filter((o) => o.estado === e).length] as const).filter(([, n]) => n > 0);
  return [...por.map(([rot, n]) => `${n} ${rot}`), ...p.ordens.filter((o) => o.tipo !== 'venda').map((o) => `${o.numero} ${o.situacao.toLowerCase()}`)].join(' · ') || 'nenhuma';
};

/** Linhas do Pedido que contam para o parceiro filtrado ('' = todos; 'sem' = sem parceiro faturador). */
const linhasDoGrupo = (p: PedidoResumo, grupo: string) => p.linhas.filter((l) => !grupo || (grupo === 'sem' ? !l.grupoId : l.grupoId === grupo));
/** P3-35: linhas conciliadas do Pedido que contam para o parceiro filtrado (mesma regra das linhas econômicas). */
const conciliadasDoGrupo = (p: PedidoResumo, grupo: string) => (p.conciliacao ?? []).filter((l) => !grupo || (grupo === 'sem' ? !l.grupoId : l.grupoId === grupo));
/** Valor · custo · margem de um total conciliado, para os cards e a lista (percentual agregado; traço sem base). */
const pctOuTraco = (x: Valores) => {
  const p = percentualDe(x);
  return p === null ? '—' : pct(p);
};

/**
 * Lista de Pedidos — filtros, indicadores e tabela ÚNICOS, usados na Lista
 * geral e na aba Pedidos do Cliente (já filtrada para ele), para as duas
 * visões nunca divergirem. `resumoDepois`: no Cliente, o resumo vem abaixo.
 */
export function ListaPedidosBase({ base, ocultarCliente, resumoDepois, teste = 'lista-pedidos', indicadoresTeste = 'indicadores-pedidos', filial, aoLimparFilial }: { base: PedidoResumo[]; ocultarCliente?: boolean; resumoDepois?: boolean; teste?: string; indicadoresTeste?: string; /** P2-23: campo Filial/unidade (aba do Cliente) logo após a busca */ filial?: ReactNode; aoLimparFilial?: () => void }) {
  const [grupo, setGrupo] = useState('');
  const [cliente, setCliente] = useState('');
  const [tipo, setTipo] = useState<'' | Tipo>('');
  const [situacoes, setSituacoes] = useState<string[]>(TODOS_STATUS_PED);
  // P2-23: na aba do Cliente, Status é critério (chip); vazio = todos
  const [statusCriterio, setStatusCriterio] = useState<string[]>([]);
  const ord = useOrdenacao<ColPed>('criacao', 'desc');
  const [busca, setBusca] = useState('');
  const per = usePeriodo<DataPed>('criacao');
  const hoje = hojeIso();
  // P2-03/P2-04 (D7): colunas e visões — Pedidos (geral) e aba Pedidos do Cliente
  const pers = usePersonalizacao(ocultarCliente ? 'cliente-pedidos' : 'pedidos', { busca: { valor: busca, definir: setBusca },
    filtros: { grupo, cliente, tipo, situacoes, statusCriterio, periodo: per.periodo, data: per.data },
    aplicarFiltros: (e: Record<string, unknown>) => {
      setGrupo((e.grupo as string) ?? '');
      setCliente((e.cliente as string) ?? '');
      setTipo((e.tipo as '' | Tipo) ?? '');
      setSituacoes((e.situacoes as string[]) ?? TODOS_STATUS_PED);
      setStatusCriterio((e.statusCriterio as string[]) ?? []);
      if (e.periodo) per.setPeriodo(e.periodo as typeof per.periodo);
      if (e.data) per.setData(e.data as DataPed);
    },
    ordenacao: ord,
  });
  const limpar = () => {
    setGrupo('');
    setCliente('');
    setTipo('');
    setSituacoes(TODOS_STATUS_PED);
    setStatusCriterio([]);
    aoLimparFilial?.();
    setBusca('');
    per.setPeriodo(periodoInicial(hoje));
    per.setData('criacao');
  };
  const clientes = [...new Map(base.map((p) => [p.cliente.id, p.cliente])).values()].sort((a, b) => a.nome.localeCompare(b.nome));
  const parceiros = [...new Map(base.flatMap((p) => p.parceiros.map((g) => [g.id, g] as const))).values()].sort((a, b) => a.nome.localeCompare(b.nome));
  const linhas = (p: PedidoResumo) => linhasDoGrupo(p, grupo);
  const achados = buscar(base, (p) => p.chaves, busca);
  const motivo = new Map((achados ?? []).map((r) => [r.item.id, textoMotivo(r, 'pedido')]));
  // Pacote 4 (bloco 2): o universo sem o filtro de Tipo alimenta as contagens do seletor (Todos · Venda · Locação · Trade-in)
  const semTipo = (achados ? achados.map((r) => r.item) : base).filter((p) => linhas(p).length > 0 && (!cliente || p.cliente.id === cliente) && situacoes.includes(SLUG_PED[p.situacao]) && (!statusCriterio.length || statusCriterio.includes(SLUG_PED[p.situacao])) && noPeriodo(per.periodo, per.data === 'criacao' ? p.geradoEm.slice(0, 10) : p.dataFechamento, hoje));
  const filtrados = semTipo.filter((p) => !tipo || p.tipo === tipo);
  const lista = ord.ordenar(
    filtrados,
    {
      numero: (p) => p.numero,
      cliente: (p) => p.cliente.nome,
      tipo: (p) => TIPOS[p.tipo],
      proposta: (p) => `${p.proposta.numero} v${p.versao}`,
      parceiros: (p) => p.parceiros.map((g) => g.nome).join(', '),
      criacao: (p) => p.geradoEm,
      fechamento: (p) => p.dataFechamento,
      vendido: (p) => totalizar(conciliadasDoGrupo(p, grupo)).vendidoLiquido.v,
      custo: (p) => totalizar(conciliadasDoGrupo(p, grupo)).vendidoLiquido.c,
      margem: (p) => margemDe(totalizar(conciliadasDoGrupo(p, grupo)).vendidoLiquido),
      margemPct: (p) => percentualDe(totalizar(conciliadasDoGrupo(p, grupo)).vendidoLiquido) ?? -Infinity,
      situacao: (p) => SITUACOES_PED.indexOf(p.situacao),
      ordens: (p) => p.ordens.length,
    },
    (a, b) => b.numero.localeCompare(a.numero),
  );
  // P2-13 (D10): os 4 status principais sempre (inclusive zero) e "saldo cancelado"/"parcialmente cancelado" só quando houver.
  // P4 bloco 3 (I07/I08): todos os Status, inclusive com zero, na ordem aprovada
  const porSituacao = SITUACOES_PED.map((s) => [s, lista.filter((p) => p.situacao === s).length] as const);
  const resumo = (
    <ResumoPedidosPorTipo
      lista={lista}
      semTipo={semTipo}
      tipo={tipo}
      setTipo={setTipo}
      grupo={grupo}
      porSituacao={porSituacao}
      teste={indicadoresTeste}
    />
  );
  return (
    <>
      {!resumoDepois && resumo}
      {/* P2-16: a busca é o primeiro campo da barra de filtros; a ajuda "?" fica à direita */}
      <BarraFiltros
        teste="pedidos"
        direita={<>{pers.controles}<Ajuda titulo="Como os Pedidos são formados">Pedidos nascem só do ganho de uma Oportunidade. Margem = venda − custo; % sobre a venda. Cada item conta uma vez; com um parceiro filtrado, somam-se só os itens faturados por ele.</Ajuda></>}
        fixos={
          <>
            <CampoBusca valor={busca} setValor={setBusca} rotulo="Buscar pedido" ajuda="PED-, cliente, CNPJ ou OC" dica="PED- ou número, Oportunidade, Proposta, OC do Cliente, Cliente, razão social ou CNPJ" teste="busca-pedidos" total={achados ? lista.length : null} />
            {filial}
            {!ocultarCliente && <FiltroMultiplo rotulo="Status" opcoes={OPCOES_STATUS_PED} valor={situacoes} padrao={TODOS_STATUS_PED} aoAplicar={setSituacoes} teste="filtro-situacao-pedido" prefixo="status-pedido" rotuloGrupo="Status do Pedido" />}
            <FiltroPeriodo periodo={per.periodo} setPeriodo={per.setPeriodo} datas={DATAS_PED} data={per.data} setData={per.setData} semData={ocultarCliente} />
          </>
        }
        opcionais={[
          ...(ocultarCliente ? [] : [filtroSelecao('cliente', 'Cliente', cliente, setCliente, clientes, 'filtro-cliente-pedidos', 'Todos os clientes')]),
          ...(ocultarCliente
            ? [
                filtroVariosComAplicar('status', 'Status', statusCriterio, setStatusCriterio, OPCOES_STATUS_PED, 'filtro-status-pedido-cliente'),
                {
                  id: 'data',
                  rotulo: 'Data considerada',
                  ativo: per.data !== 'criacao',
                  resumo: DATAS_PED.find((d) => d.id === per.data)?.rotulo.replace('Data de ', '').replace(/^./, (x) => x.toUpperCase()) ?? '',
                  controle: (
                    <label>
                      Data considerada
                      <select value={per.data} onChange={(e) => per.setData(e.target.value as DataPed)} data-teste="data-considerada">
                        {DATAS_PED.map((x) => (
                          <option key={x.id} value={x.id}>
                            {x.rotulo}
                          </option>
                        ))}
                      </select>
                    </label>
                  ),
                  limpar: () => per.setData('criacao'),
                },
              ]
            : []),
          filtroSelecao('tipo', 'Tipo', tipo, (v) => setTipo(v as '' | Tipo), (Object.keys(TIPOS) as Tipo[]).map((x) => ({ id: x, nome: TIPOS[x] })), 'filtro-tipo-pedido'),
          filtroSelecao('parceiro', 'Parceiro comercial', grupo, setGrupo, [...parceiros, { id: 'sem', nome: 'Sem parceiro definido' }], 'filtro-grupo', 'Todos os parceiros'),
        ]}
        aoLimpar={limpar}
      />
      {lista.length === 0 ? (
        <p className="vazio" data-teste={`${teste}-vazia`}>
          {situacoes.length === 0 ? 'Nenhum status selecionado — use Todas ou escolha ao menos um.' : <>Nenhum Pedido {ocultarCliente ? 'deste cliente ' : ''}com {busca.trim() ? 'a busca e ' : ''}os filtros atuais. Pedidos nascem só do ganho de uma Oportunidade.</>}
        </p>
      ) : (
        <div className="rolagem quadro-tabela">
          <table ref={pers.tabela} className="tabela tabela-pedidos" data-teste={teste}>
            <thead>
              <tr>
                <Th ord={ord} coluna="numero" padrao="desc" className="col-fixa">
                  Pedido
                </Th>
                {!ocultarCliente && (
                  <Th ord={ord} coluna="cliente">
                    Cliente / CNPJ faturado
                  </Th>
                )}
                <Th ord={ord} coluna="tipo">Tipo</Th>
                <Th ord={ord} coluna="proposta">Proposta aceita</Th>
                <Th ord={ord} coluna="parceiros">Parceiro(s)</Th>
                <Th ord={ord} coluna="criacao" padrao="desc">Criação</Th>
                <Th ord={ord} coluna="fechamento" padrao="desc">Fechamento</Th>
                <Th ord={ord} coluna="vendido" padrao="desc" numero>Vendido líquido</Th>
                <Th ord={ord} coluna="custo" padrao="desc" numero>Custo</Th>
                <Th ord={ord} coluna="margem" padrao="desc" numero>Margem (R$)</Th>
                <Th ord={ord} coluna="margemPct" padrao="desc" numero>Margem (%)</Th>
                <Th ord={ord} coluna="situacao">Status</Th>
                <Th ord={ord} coluna="ordens" padrao="desc" numero>Ordens</Th>
              </tr>
            </thead>
            <tbody>
              {lista.map((p) => {
                const vl = totalizar(conciliadasDoGrupo(p, grupo)).vendidoLiquido;
                const bruta = margemDe(vl);
                return (
                  <tr key={p.id}>
                    <td className="col-fixa nowrap">
                      <a href={`#/pedidos/${p.numero}`}>{p.numero}</a>
                      {motivo.get(p.id) && (
                        <span className="motivo-busca" data-teste={`motivo-${p.numero}`}>
                          {motivo.get(p.id)}.
                        </span>
                      )}
                    </td>
                    {!ocultarCliente && (
                      <td>
                        <span className="duas-linhas" title={p.cliente.nome}>
                          {p.cliente.nome}
                        </span>
                        <div className="texto-secundario nowrap">{p.faturamento.cnpj}</div>
                      </td>
                    )}
                    <td>
                      <SeloTipo tipo={p.tipo} />
                    </td>
                    <td className="nowrap">
                      {p.proposta.numero} · v{p.versao}
                    </td>
                    <td>
                      <span className="duas-linhas">{p.parceiros.map((g) => g.nome).join(', ') || <span className="ausente">—</span>}</span>
                    </td>
                    <td className="nowrap">{dataBr(p.geradoEm.slice(0, 10))}</td>
                    <td className="nowrap">{p.dataFechamento ? dataBr(p.dataFechamento) : <span className="ausente">—</span>}</td>
                    <td className="nowrap numero">{moeda(vl.v)}</td>
                    <td className="nowrap numero">{moeda(vl.c)}</td>
                    <td className={`nowrap numero${bruta < 0 ? ' negativo' : ''}`}>{moeda(bruta)}</td>
                    <td className={`nowrap numero${bruta < 0 ? ' negativo' : ''}`}>{pctOuTraco(vl)}</td>
                    <td data-teste={`situacao-pedido-${p.numero}`}>
                      <SeloSituacao texto={p.situacao} tom={tomPedido(p.situacao)} />
                      {p.tipo !== 'venda' && <span className="texto-secundario"> (ilustrativa)</span>}
                    </td>
                    <td className="numero" title={resumoOrdens(p)}>
                      {p.ordens.length}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="rodape-tabela" data-teste={`${teste}-rodape`}>
            {lista.length} pedido(s)
          </div>
        </div>
      )}
    </>
  );
}

const TIPOS_SELETOR: { id: '' | Tipo; nome: string }[] = [
  { id: '', nome: 'Todos' },
  { id: 'venda', nome: 'Venda' },
  { id: 'locacao', nome: 'Locação' },
  { id: 'trade_in', nome: 'Trade-in' },
];
const COR_SITUACAO: Record<SituacaoPedido, string> = { 'Em andamento': 'aberto', 'Parcialmente cancelado': 'parcial', 'Faturado parcialmente': 'parcial-fat', 'Faturado parcialmente · saldo cancelado': 'saldo', Faturado: 'faturado', Cancelado: 'cancelado' };

/**
 * Pacote 4 (bloco 2, mockup aprovado em 17/10/2026) — cabeçalho da listagem com as BASES SEPARADAS por modalidade:
 * Venda (vendido líquido e margem prevista da D4), Locação (mensalidades de cobrança mensal e total previsto, sem
 * misturar períodos) e Trade-in (avaliação bruta, valor ao Cliente e margem bruta). "Todos" mostra contagens e cada
 * base em separado — nenhum total financeiro soma bases diferentes. Fórmulas de cada modalidade inalteradas.
 */
function ResumoPedidosPorTipo({ lista, semTipo, tipo, setTipo, grupo, porSituacao, teste }: { lista: PedidoResumo[]; semTipo: PedidoResumo[]; tipo: '' | Tipo; setTipo: (t: '' | Tipo) => void; grupo: string; porSituacao: (readonly [SituacaoPedido, number])[]; teste: string }) {
  const doTipo = (t: Tipo) => lista.filter((p) => p.tipo === t);
  const total = (ps: PedidoResumo[]) => totalizar(ps.flatMap((p) => conciliadasDoGrupo(p, grupo)));
  const venda = total(doTipo('venda'));
  const loc = doTipo('locacao');
  const locT = total(loc).vendidoLiquido;
  const ativas = loc.filter((p) => !p.cancelamento && p.locacao);
  const mensais = ativas.filter((p) => p.locacao!.periodicidade === 'Mensal');
  const mensalidades = mensais.reduce((a, p) => a + p.locacao!.parcela, 0);
  const outras = ativas.length - mensais.length;
  const tri = total(doTipo('trade_in')).vendidoLiquido;
  const ocPend = lista.filter((p) => p.ocPendente).length;
  const declarados = lista.filter((p) => p.ocNaoSeAplica).length;
  const ajudaOc = <Ajuda titulo="OC pendente">{`Pedidos sem OC do Cliente vigente. Não contam os ${declarados} Pedido(s) com a declaração “Não se aplica” ativa.`}</Ajuda>;
  const cont = (t: '' | Tipo) => (t ? semTipo.filter((p) => p.tipo === t).length : semTipo.length);
  const cardPedidos = <CardLista icone="pedidos" tom="azul" rotulo="Pedidos" valor={lista.length} teste="card-qtd-pedidos" />;
  const cardOc = <CardLista icone="documento" tom="roxo" rotulo="OC pendente" valor={ocPend} ajuda={ajudaOc} teste="card-oc-pendente" />;
  const pctTexto = (x: Valores) => pctOuTraco(x);
  return (
    <section className="resumo-destaque resumo-pedidos-tipos" data-teste={teste} aria-label="Resumo dos Pedidos">
      <div className="seletor-tipo-pedido" role="group" aria-label="Modalidade" data-teste="seletor-tipo-pedido">
        {TIPOS_SELETOR.map((t) => (
          <button key={t.id || 'todos'} type="button" className={tipo === t.id ? 'ativo' : ''} aria-pressed={tipo === t.id} onClick={() => setTipo(t.id)} data-teste={`seletor-tipo-${t.id || 'todos'}`}>
            {t.nome} <b>{cont(t.id)}</b>
          </button>
        ))}
      </div>
      {tipo === '' && (
        <>
          <CardsLista n={2}>
            {cardPedidos}
            {cardOc}
          </CardsLista>
          <div className="quadros-modalidade">
            <section className="quadro-modalidade" data-teste="quadro-venda">
              <h3>
                Venda
              </h3>
              <dl>
                <dt>Vendido líquido</dt>
                <dd>{moeda2(venda.vendidoLiquido.v)}</dd>
                <dt>Margem prevista</dt>
                <dd>
                  {moeda2(margemDe(venda.vendidoLiquido))} ({pctTexto(venda.vendidoLiquido)})
                </dd>
              </dl>
            </section>
            <section className="quadro-modalidade" data-teste="quadro-locacao">
              <h3>
                Locação
              </h3>
              <dl>
                <dt>Mensalidades</dt>
                <dd>{moeda2(mensalidades)}/mês</dd>
                <dt>Total previsto</dt>
                <dd>{moeda2(locT.v)}</dd>
              </dl>
            </section>
            <section className="quadro-modalidade" data-teste="quadro-tradein">
              <h3>
                Trade-in
              </h3>
              <dl>
                <dt>Avaliação bruta</dt>
                <dd>{moeda2(tri.v)}</dd>
                <dt>Margem bruta</dt>
                <dd>
                  {moeda2(margemDe(tri))} ({pctTexto(tri)})
                </dd>
              </dl>
            </section>
          </div>
        </>
      )}
      {tipo === 'venda' && (
        <>
          <CardsLista n={4}>
            {cardPedidos}
            <CardLista icone="dinheiro" tom="azul" rotulo="Vendido líquido" valor={moeda(venda.vendidoLiquido.v)} detalhe={`${venda.vendidoLiquido.q} un. · custo ${moeda(venda.vendidoLiquido.c)}`} teste="card-vendido-liquido" ajuda={<Ajuda titulo="Vendido líquido">{`Vendido original + ajuste por retificação − saldo cancelado sem substituta (corte de hoje; sem frete e sem abatimento). Confere com faturado + carteira nas Ordens + valor ainda sem Ordem (${moeda(venda.semOrdem.v)}). O saldo substituído por outra Ordem não reduz a venda. Nos cards, os valores são arredondados para reais inteiros apenas na exibição; a Composição do vendido aberta mostra os centavos e fecha exatamente.`}</Ajuda>} />
            <CardLista icone="grafico" tom="verde" rotulo="Margem prevista" valor={moeda(margemDe(venda.vendidoLiquido))} detalhe={pctTexto(venda.vendidoLiquido)} teste="card-margem-prevista" ajuda={<Ajuda titulo="Margem prevista">Vendido líquido − custo correspondente; o percentual é a margem agregada sobre o valor agregado (traço quando não há base). Valor arredondado para reais inteiros apenas na exibição; o cálculo usa os centavos.</Ajuda>} />
            {cardOc}
          </CardsLista>
          <details className="composicao-vendido" data-teste="composicao-vendido">
            <summary>
              <span className="titulo-composicao">Composição do vendido</span>
              <Ajuda titulo="Composição do vendido">Vendido líquido = vendido original + ajuste assinado da retificação − saldo cancelado sem substituição (magnitude positiva). A substituição não desconta duas vezes. Revisões válidas na data de corte de hoje. Aberta, mostra os valores com centavos; nos cards e na linha resumida, os valores são arredondados para reais inteiros apenas na exibição.</Ajuda>
              <span className="resumo-inline" data-teste="composicao-inline">
                Original {moeda(venda.original.v)} · Ajuste {moeda(venda.ajuste.v)} · Cancelado {moeda(venda.cancelado.v)}
              </span>
            </summary>
            <table className="tabela tabela-compacta tabela-composicao" data-teste="tabela-composicao">
              <thead>
                <tr>
                  <th>Componente</th>
                  <th className="numero">Valor (R$)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Vendido original</td>
                  <td className="numero" data-teste="card-vendido-original">{moeda2(venda.original.v)}</td>
                </tr>
                <tr>
                  <td>(±) Ajustes por retificação</td>
                  <td className="numero" data-teste="card-ajuste-retificacao">{moeda2(venda.ajuste.v)}</td>
                </tr>
                <tr>
                  <td>(−) Saldo cancelado sem substituição</td>
                  <td className="numero" data-teste="card-saldo-cancelado">{moeda2(venda.cancelado.v)}</td>
                </tr>
                <tr className="linha-total">
                  <td>Vendido líquido</td>
                  <td className="numero" data-teste="composicao-vendido-liquido">{moeda2(venda.vendidoLiquido.v)}</td>
                </tr>
              </tbody>
            </table>
          </details>
        </>
      )}
      {tipo === 'locacao' && (
        <>
          <CardsLista n={5}>
            {cardPedidos}
            <CardLista icone="relogio" tom="azul" rotulo="Mensalidades" valor={moeda2(mensalidades)} detalhe="por mês · contratos de cobrança mensal" teste="card-mensalidades" ajuda={<Ajuda titulo="Mensalidades">Soma das parcelas do Cliente dos contratos com cobrança MENSAL ainda válidos. Contratos de outra periodicidade não entram aqui (contagem abaixo) — os períodos não são misturados.</Ajuda>} />
            <CardLista icone="dinheiro" tom="verde" rotulo="Total previsto" valor={moeda2(locT.v)} detalhe="base contratada, sem reajustes" teste="card-total-previsto" ajuda={<Ajuda titulo="Total previsto">Parcela × cobranças de cada contrato, na base contratada, sem projetar reajustes. Pedido cancelado não entra.</Ajuda>} />
            <CardLista icone="grafico" tom="roxo" rotulo="Margem bruta total" valor={moeda2(margemDe(locT))} detalhe={`${pctTexto(locT)} do total previsto`} teste="card-margem-locacao" ajuda={<Ajuda titulo="Margem bruta total">Total previsto − valor líquido dos parceiros de Locação (comissão total); percentual agregado sobre o total previsto.</Ajuda>} />
            {cardOc}
          </CardsLista>
          <p className="faixa-outras-periodicidades" data-teste="outras-periodicidades">
            Outras periodicidades: {outras}
            <Ajuda titulo="Outras periodicidades">Contratos com cobrança trimestral, semestral ou anual: entram no Total previsto e na Margem bruta total, mas não em Mensalidades.</Ajuda>
          </p>
        </>
      )}
      {tipo === 'trade_in' && (
        <CardsLista n={5}>
          {cardPedidos}
          <CardLista icone="moedas" tom="azul" rotulo="Avaliação bruta" valor={moeda2(tri.v)} detalhe="avaliação dos compradores" teste="card-avaliacao-bruta" ajuda={<Ajuda titulo="Avaliação bruta">Soma das avaliações dos compradores para os equipamentos ganhos. Não é venda da Inttrix.</Ajuda>} />
          <CardLista icone="dinheiro" tom="verde" rotulo="Valor ao Cliente" valor={moeda2(tri.c)} detalhe="a receber pelo Cliente" teste="card-valor-cliente" ajuda={<Ajuda titulo="Valor ao Cliente">Soma dos valores destinados aos Clientes (recebidos por transferência bancária).</Ajuda>} />
          <CardLista icone="grafico" tom="roxo" rotulo="Margem bruta" valor={moeda2(margemDe(tri))} detalhe={`${pctTexto(tri)} da avaliação`} teste="card-margem-tradein" ajuda={<Ajuda titulo="Margem bruta">Avaliação bruta − valor ao Cliente; percentual agregado sobre a avaliação bruta.</Ajuda>} />
          {cardOc}
        </CardsLista>
      )}
      <ChipsStatus itens={porSituacao.map(([sit, n]) => ({ nome: sit, n, cor: COR_SITUACAO[sit] }))} teste="status-pedidos-card" />
    </section>
  );
}

export function ListaPedidos() {
  const carga = useCarga(() => api.get<PedidoResumo[]>('/api/pedidos'), []);
  return (
    <>
      {carga.dados ? <ListaPedidosBase base={carga.dados} /> : <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="pedidos" />}
    </>
  );
}

/** Aba Pedidos do Cliente: a mesma lista, filtrada para ele, com o resumo abaixo. */
export function PedidosDoCliente({ clienteId, entidadeId, filial, aoLimparFilial }: { clienteId: string; entidadeId?: string; filial?: ReactNode; aoLimparFilial?: () => void }) {
  const carga = useCarga(() => api.get<PedidoResumo[]>(`/api/pedidos${consulta({ cliente_id: clienteId, entidade_id: entidadeId })}`), [clienteId, entidadeId]);
  if (!carga.dados) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="pedidos-cliente" />;
  return <ListaPedidosBase base={carga.dados} ocultarCliente resumoDepois teste="pedidos-cliente" indicadoresTeste="indicadores-pedidos-cliente" filial={filial} aoLimparFilial={aoLimparFilial} />;
}

/**
 * Rastreabilidade navegável: Oportunidade → Proposta (versão aceita) → Pedido →
 * OC do Cliente → Ordens. A mesma cadeia aparece na Oportunidade, na Proposta e no Pedido, com o elo atual destacado.
 */
export function CadeiaRastreabilidade({ pedido: p, atual }: { pedido: Pick<PedidoResumo, 'numero' | 'oportunidade' | 'proposta' | 'versao' | 'ocsVigentes' | 'ordens'>; atual: 'oportunidade' | 'proposta' | 'pedido' }) {
  return (
    <nav className="cadeia-rastreabilidade" aria-label="Rastreabilidade do Pedido" data-teste="rastreabilidade">
      <ol className="elos" data-teste={`cadeia-${p.numero}`}>
        <li aria-current={atual === 'oportunidade' ? 'step' : undefined}>
          <span className="rotulo-elo">Oportunidade de origem</span>
          <a href={`#/oportunidades/${p.oportunidade.id}`} data-teste="elo-oportunidade">
            #{p.oportunidade.numero} — {p.oportunidade.titulo}
          </a>
        </li>
        <li aria-current={atual === 'proposta' ? 'step' : undefined}>
          <span className="rotulo-elo">Proposta de origem (aceita)</span>
          <a href={`#/oportunidades/${p.oportunidade.id}/propostas/${p.proposta.id}/v/${p.versao}`} data-teste="elo-proposta">
            {p.proposta.numero} — v{p.versao}
          </a>
        </li>
        <li aria-current={atual === 'pedido' ? 'step' : undefined}>
          <span className="rotulo-elo">Pedido gerado</span>
          <a href={`#/pedidos/${p.numero}`} data-teste="elo-pedido">
            {p.numero}
          </a>
        </li>
        <li>
          <span className="rotulo-elo">OC do Cliente</span>
          {p.ocsVigentes.length ? <span>{p.ocsVigentes.join(' · ')}</span> : <span className="texto-secundario">pendente</span>}
        </li>
        <li>
          <span className="rotulo-elo">Ordens</span>
          {p.ordens.length ? (
            <span>
              {p.ordens.map((o, i) => (
                <span key={o.numero}>
                  {i > 0 && ' · '}
                  <a href={`#/ordens/${o.numero}`}>{o.numero}</a>
                </span>
              ))}
            </span>
          ) : (
            <span className="texto-secundario">nenhuma ainda</span>
          )}
        </li>
      </ol>
    </nav>
  );
}

/** Cadeia do Pedido de uma Oportunidade (na Oportunidade e na Proposta): carrega o Pedido pelo número. */
export function CadeiaDaOportunidade({ numeroPedido, atual }: { numeroPedido: string; atual: 'oportunidade' | 'proposta' }) {
  const { pode } = useSessao();
  const carga = useCarga(() => (pode('visualizar', 'pedido') ? api.get<PedidoCompleto>(`/api/pedidos/${numeroPedido}`) : Promise.resolve(null)), [numeroPedido]);
  if (!carga.dados) return null;
  return <CadeiaRastreabilidade pedido={carga.dados} atual={atual} />;
}

/** Abatimento congelado na geração (não configurado no envio: nada é suposto nem recalculado). */
const abatimentoDe = (c: ConteudoPedido) => (c.abatimento && !c.abatimento.naoConfigurado ? c.abatimento : null);
function AjudaLiquida({ c }: { c: ConteudoPedido }) {
  const abat = abatimentoDe(c);
  return (
    <Ajuda titulo="Comissão líquida estimada">
      {abat ? `Comissão bruta − abatimento de ${pctNum(abat.percentual)} vigente no envio da versão (desde ${dataBr(abat.vigenteDesde)}). Valor interno, congelado; não é recalculado.` : 'Sem abatimento vigente no envio da versão: a comissão líquida estimada não é calculada.'}
    </Ajuda>
  );
}
const liquidaTexto = (c: ConteudoPedido, base: number) => {
  const abat = abatimentoDe(c);
  return abat ? moeda2(liquidaEstimada(base, abat.percentual)) : 'abatimento não configurado na geração';
};
/** Pacote 4: endereço para a TELA, só com as partes informadas (cadastros incompletos não viram ", — —"); PDFs não mudam. */
const enderecoTela = (e: LocalCongelado['endereco']) => {
  const rua = [e.logradouro, e.numero].filter((x) => String(x ?? '').trim()).join(', ');
  const cidade = [e.cidade, e.uf].filter((x) => String(x ?? '').trim()).join('/');
  return [rua, e.complemento, e.bairro, cidade, String(e.cep ?? '').trim() && `CEP ${e.cep}`].filter((x) => String(x ?? '').trim()).join(' — ') || '—';
};
/** Pacote 4: frete em forma curta ("CIF · Incluso"); o texto completo continua no "?" e nos documentos. */
const PAGADOR_CURTO: Record<keyof typeof PAGADORES, string> = { incluso: 'Incluso', inttrix: 'Inttrix', cliente: 'Cliente' };
const freteCurto = (f: ConteudoPedido['frete']) => `${f.modalidade} · ${PAGADOR_CURTO[f.pagador]}`;

/**
 * Pacote 4 — "Resumo do pedido". Venda: 4 cards compactos (Venda, Custo, Margem bruta, Comissão líquida estimada) e
 * pagamento, prazo e frete numa faixa enxuta; o restante vai para "Detalhes financeiros". Locação e Trade-in mantêm
 * os grupos próprios (os cards de Venda não se aplicam a eles).
 */
function ResumoPedido({ pedido: p }: { pedido: PedidoCompleto }) {
  const c = p.conteudo;
  const titulo = (
    <h2 id="t-resumo-pedido">
      Resumo do pedido
      <Ajuda titulo="Resumo do pedido">{`Valores da versão aceita (Proposta ${p.proposta.numero} — v${p.versao}), congelados no Pedido. Correções só por “Reabrir para correção”, com histórico.`}</Ajuda>
    </h2>
  );
  if (!c.locacao && !c.tradein) {
    const eco = economia(c.itens.map((i) => ({ quantidade: i.quantidade, custoUnit: i.custoUnit, vendaUnit: i.precoUnit })), c.frete);
    return (
      <section className="resumo-pedido" data-teste="economia-pedido" aria-labelledby="t-resumo-pedido">
        {titulo}
        <div className="cards-resumo-pedido">
          <div className="card-resumo tom-venda" data-teste="resumo-venda">
            <span className="rotulo-card">Venda</span>
            <strong>{moeda2(eco.totalProdutos)}</strong>
          </div>
          <div className="card-resumo tom-custo" data-teste="resumo-custo">
            <span className="rotulo-card">Custo</span>
            <strong>{moeda2(eco.custo)}</strong>
          </div>
          <div className="card-resumo tom-margem" data-teste="resumo-margem">
            <span className="rotulo-card">Margem bruta</span>
            <strong>
              {moeda2(eco.bruta)} <span className="pct-card">({pct(eco.percentual)})</span>
            </strong>
          </div>
          <div className="card-resumo tom-liquida" data-teste="resumo-liquida">
            <span className="rotulo-card">
              Comissão líquida estimada
              <AjudaLiquida c={c} />
            </span>
            {abatimentoDe(c) ? <strong data-teste="liquida-pedido">{liquidaTexto(c, eco.aposFrete)}</strong> : <strong className="valor-indisponivel" data-teste="liquida-pedido">Não calculada</strong>}
          </div>
        </div>
        <dl className="faixa-condicoes" data-teste="condicoes-pedido">
          <div>
            <Icone nome="fatura" />
            <dt>Pagamento:</dt>
            <dd>{pagamentoTexto(c.condicoes)}</dd>
          </div>
          <div>
            <Icone nome="relogio" />
            <dt>Prazo:</dt>
            <dd>{c.condicoes.prazoGeral || '—'}</dd>
          </div>
          <div>
            <Icone nome="caminhao" />
            <dt>Frete:</dt>
            <dd title={PAGADORES[c.frete.pagador]}>
              {freteCurto(c.frete)} · {moeda2(c.frete.valor)}
            </dd>
          </div>
        </dl>
      </section>
    );
  }
  if (c.locacao) {
    const L = c.locacao;
    const m = margemLocacao(c);
    const abat = abatimentoDe(c);
    const naoSeAplica = (
      <strong className="valor-indisponivel" data-teste="liquida-pedido">
        Não se aplica
      </strong>
    );
    return (
      <section className="resumo-pedido" data-teste="economia-pedido" aria-labelledby="t-resumo-pedido">
        {titulo}
        <div className="cards-resumo-pedido cinco" data-teste="cards-locacao">
          <div className="card-resumo tom-venda" data-teste="resumo-prazo">
            <span className="rotulo-card">Prazo</span>
            <strong>{L.prazoMeses} meses</strong>
          </div>
          <div className="card-resumo tom-custo" data-teste="resumo-mensalidade">
            <span className="rotulo-card">{m.mensal ? 'Mensalidade' : `Parcela ${L.periodicidade.toLowerCase()}`}</span>
            <strong>{moeda2(L.clienteParcela)}</strong>
          </div>
          <div className="card-resumo tom-margem" data-teste="resumo-total-previsto">
            <span className="rotulo-card">
              Total previsto
              <Ajuda titulo="Total previsto">{`${L.cobrancas} ${L.cobrancas === 1 ? 'cobrança' : 'cobranças'} de ${moeda2(L.clienteParcela)} na base contratada, sem projetar reajustes. Valor interno do Pedido.`}</Ajuda>
            </span>
            <strong>{moeda2(L.totalContrato)}</strong>
          </div>
          <div className="card-resumo tom-venda" data-teste="resumo-margem">
            <span className="rotulo-card">
              {m.rotuloMargem}
              <Ajuda titulo={m.rotuloMargem}>{m.ajuda}</Ajuda>
            </span>
            {m.porParcela ? (
              <strong>
                {moeda2(L.comissaoParcela)} <span className="pct-card">({pct(L.clienteParcela ? L.comissaoParcela / L.clienteParcela : 0)})</span>
              </strong>
            ) : (
              <strong className="valor-indisponivel">Não se aplica</strong>
            )}
          </div>
          <div className="card-resumo tom-custo" data-teste="resumo-liquida">
            <span className="rotulo-card">
              {m.rotuloLiquida}
              <AjudaLiquida c={c} />
            </span>
            {!m.porParcela ? (
              naoSeAplica
            ) : abat ? (
              <strong data-teste="liquida-pedido">{moeda2(liquidaEstimada(L.comissaoParcela, abat.percentual))}</strong>
            ) : (
              <strong className="valor-indisponivel" data-teste="liquida-pedido">
                Não calculada
              </strong>
            )}
          </div>
        </div>
        <dl className="faixa-condicoes duas" data-teste="condicoes-pedido">
          <div>
            <Icone nome="relogio" />
            <dt>Regra de margem:</dt>
            <dd data-teste="regra-margem-pedido">{m.regraCurta}</dd>
          </div>
          <div>
            <Icone nome="grafico" />
            <dt>Reajuste:</dt>
            <dd data-teste="reajuste-pedido">{L.reajuste ? textoReajuste(L.reajuste) : 'não definido na versão'}</dd>
          </div>
        </dl>
      </section>
    );
  }
  const T = c.tradein!;
  const abatT = abatimentoDe(c);
  const rec = recebimentoTradein(c.condicoes);
  const conf = T.confirmacaoBancaria;
  return (
    <section className="resumo-pedido" data-teste="economia-pedido" aria-labelledby="t-resumo-pedido">
      {titulo}
      <div className="cards-resumo-pedido" data-teste="cards-tradein">
        <div className="card-resumo tom-venda" data-teste="resumo-avaliacao">
          <span className="rotulo-card">
            Avaliação bruta
            <Ajuda titulo="Avaliação bruta">Valor total avaliado pelo comprador para os equipamentos ganhos. Não é venda da Inttrix.</Ajuda>
          </span>
          <strong>{moeda2(T.bruto)}</strong>
        </div>
        <div className="card-resumo tom-custo" data-teste="resumo-valor-cliente">
          <span className="rotulo-card">
            Valor ao Cliente
            <Ajuda titulo="Valor ao Cliente">Valor destinado ao Cliente pela alienação dos equipamentos (recebido por transferência bancária).</Ajuda>
          </span>
          <strong>{moeda2(T.oferta)}</strong>
        </div>
        <div className="card-resumo tom-margem" data-teste="resumo-margem">
          <span className="rotulo-card">
            Margem bruta
            <Ajuda titulo="Margem bruta">Avaliação bruta − valor ao Cliente (comissão acordada com o comprador); o percentual é sobre a avaliação bruta.</Ajuda>
          </span>
          <strong>
            {moeda2(T.comissao)} <span className="pct-card">({pctNum(T.percentualEquivalente)})</span>
          </strong>
        </div>
        <div className="card-resumo tom-liquida" data-teste="resumo-liquida">
          <span className="rotulo-card">
            Comissão líquida estimada
            <AjudaLiquida c={c} />
          </span>
          {abatT ? <strong data-teste="liquida-pedido">{moeda2(liquidaEstimada(T.comissao, abatT.percentual))}</strong> : <strong className="valor-indisponivel" data-teste="liquida-pedido">Não calculada</strong>}
        </div>
      </div>
      <dl className={`faixa-condicoes${rec.anterior ? '' : ' quatro'}`} data-teste="condicoes-pedido">
        <div>
          {rec.anterior && <Icone nome="moedas" />}
          <dt>Recebimento do Cliente:</dt>
          <dd data-teste="recebimento-cliente">
            {rec.anterior && (
              <>
                <span className="selo selo-neutro selo-registro-anterior" data-teste="selo-registro-anterior">
                  Registro anterior
                </span>{' '}
              </>
            )}
            {rec.recebimento}
            {rec.anterior && <Ajuda titulo="Registro anterior">Pedido gerado antes do recebimento padronizado (transferência bancária e prazo em dias): forma e condição aparecem como foram gravadas na versão aceita.</Ajuda>}
          </dd>
        </div>
        <div>
          {rec.anterior && <Icone nome="relogio" />}
          <dt>Prazo de recebimento:</dt>
          <dd data-teste="prazo-recebimento">{rec.prazo}</dd>
        </div>
        {!rec.anterior && (
          <div>
            <dt>Contados a partir de:</dt>
            <dd data-teste="marco-recebimento-pedido">
              {rec.marco}
              <Ajuda titulo="Prazo de recebimento">Dias para o Cliente receber o valor destinado a ele, contados a partir do evento combinado na negociação. O vencimento só é calculado depois do registro da data efetiva desse evento (não pela data da Proposta, do Pedido nem da OTI).</Ajuda>
            </dd>
          </div>
        )}
        <div>
          <span className={`marca-confirmacao${conf ? ' ok' : ''}`} aria-hidden="true">
            {conf ? '✓' : '!'}
          </span>
          <dt>Dados bancários:</dt>
          <dd data-teste="dados-bancarios-pedido">
            {conf ? 'Confirmados' : 'Não confirmados'}
            <Ajuda titulo="Dados bancários">Os dados bancários não ficam no HUB: o HUB registra só quem confirmou e quando (fora do HUB). Detalhes em “Detalhes financeiros”.</Ajuda>
          </dd>
        </div>
      </dl>
      <DetalhesFinanceiros pedido={p} />
    </section>
  );
}

/** "6 cobranças mensais", "1 cobrança trimestral" — singular/plural corretos. */
const PLURAL_PERIODO: Record<string, string> = { Mensal: 'mensais', Trimestral: 'trimestrais', Semestral: 'semestrais', Anual: 'anuais' };
const cobrancasTexto = (n: number, periodicidade: string) => (n === 1 ? `1 cobrança ${periodicidade.toLowerCase()}` : `${n} cobranças ${PLURAL_PERIODO[periodicidade] ?? periodicidade.toLowerCase()}`);

/**
 * Pacote 4 (bloco 2) — margem da Locação: mensal só quando a remuneração é por parcela/mensal E a cobrança é mensal;
 * cobrança trimestral/semestral/anual mostra "por parcela"; regra única sobre o total não vira valor mensal.
 */
function margemLocacao(c: ConteudoPedido) {
  const L = c.locacao!;
  const regra = L.regraComissao?.regra;
  const porParcela = regra ? regra === 'por_parcela' || regra === 'mensal_projeto' : L.base === 'parcela';
  const mensal = L.periodicidade === 'Mensal';
  const sufixo = mensal ? 'mensal' : 'por parcela';
  const regraCurta = regra ? `${{ por_parcela: 'Por parcela', mensal_projeto: 'Mensal', unica_projeto: 'Única (total do projeto)', unica_venda: 'Única (total da venda)' }[regra]} · ${pctNum(L.regraComissao!.percentual)}` : `${pctNum(L.percentual)} ${L.base === 'parcela' ? 'sobre a parcela' : 'sobre o total'}`;
  return {
    porParcela,
    mensal: mensal,
    regraCurta,
    rotuloMargem: porParcela ? `Margem bruta ${sufixo}` : 'Margem bruta mensal',
    rotuloLiquida: porParcela ? `Comissão líquida ${sufixo} estimada` : 'Comissão líquida mensal estimada',
    ajuda: porParcela
      ? `Comissão bruta de cada ${mensal ? 'mês' : `parcela ${L.periodicidade.toLowerCase()}`}: ${pctNum(L.regraComissao?.percentual ?? L.percentual)} da parcela do Cliente. Total do contrato em “Detalhes financeiros”.`
      : `A remuneração desta Locação é única sobre o total (${regraCurta}): não há valor mensal. Total em “Detalhes financeiros”.`,
  };
}

/** Pacote 4: demais informações financeiras da Venda, recolhidas inicialmente. */
function DetalhesFinanceiros({ pedido: p }: { pedido: PedidoCompleto }) {
  const c = p.conteudo;
  const abat = abatimentoDe(c);
  if (c.locacao) {
    const L = c.locacao;
    return (
      <details className="detalhes-financeiros" data-teste="detalhes-financeiros">
        <summary>Detalhes financeiros</summary>
        <dl className="pares-detalhes">
          <div>
            <dt>Margem bruta total</dt>
            <dd data-teste="margem-total-locacao">
              {moeda2(L.comissaoTotal)} ({pct(L.totalContrato ? L.comissaoTotal / L.totalContrato : 0)} do total previsto)
            </dd>
          </div>
          <div>
            <dt>
              Comissão líquida total estimada <AjudaLiquida c={c} />
            </dt>
            <dd data-teste="liquida-total-locacao">{abat ? moeda2(liquidaEstimada(L.comissaoTotal, abat.percentual)) : 'Não calculada'}</dd>
          </div>
          <div>
            <dt>Valor líquido do parceiro</dt>
            <dd data-teste="liquido-parceiro-locacao">{moeda2(L.liquidoTotal)}</dd>
          </div>
          <div>
            <dt>Prazo · cobranças</dt>
            <dd data-teste="prazo-cobrancas-locacao">
              {L.prazoMeses} {L.prazoMeses === 1 ? 'mês' : 'meses'} · {cobrancasTexto(L.cobrancas, L.periodicidade)}
            </dd>
          </div>
          <div>
            <dt>Regra de margem</dt>
            <dd>{L.regraComissao ? `${regraComissao(L.regraComissao.regra).nome} · ${pctNum(L.regraComissao.percentual)} (${L.regraComissao.origem})` : `${pctNum(L.percentual)} ${L.base === 'parcela' ? 'sobre a parcela' : 'sobre o total'}`}</dd>
          </div>
          <div>
            <dt>Forma de pagamento (Cliente)</dt>
            <dd>{L.formaPagamento || '—'}</dd>
          </div>
        </dl>
      </details>
    );
  }
  if (c.tradein) {
    const conf = c.tradein.confirmacaoBancaria;
    return (
      <details className="detalhes-financeiros" data-teste="detalhes-financeiros">
        <summary>Detalhes financeiros</summary>
        <dl className="pares-detalhes">
          <div>
            <dt>Abatimento</dt>
            <dd data-teste="abatimento-tradein">{abat ? `${pctNum(abat.percentual)} vigente no envio (desde ${dataBr(abat.vigenteDesde)})` : 'Não configurado na geração — comissão líquida não calculada'}</dd>
          </div>
          <div>
            <dt>Confirmação bancária</dt>
            <dd data-teste="confirmacao-bancaria-tradein">{conf ? `${conf.usuario} · ${dataBr(conf.dataHora.slice(0, 10))} · fora do HUB` : 'Pendente — os dados bancários não foram confirmados'}</dd>
          </div>
          {c.condicoes.observacao.trim() && (
            <div>
              <dt>Observação das condições</dt>
              <dd>{c.condicoes.observacao}</dd>
            </div>
          )}
        </dl>
      </details>
    );
  }
  const eco = economia(c.itens.map((i) => ({ quantidade: i.quantidade, custoUnit: i.custoUnit, vendaUnit: i.precoUnit })), c.frete);
  return (
    <details className="detalhes-financeiros" data-teste="detalhes-financeiros">
      <summary>Detalhes financeiros</summary>
      <dl className="pares-detalhes">
        <div>
          <dt>Margem após frete</dt>
          <dd>{moeda2(eco.aposFrete)}</dd>
        </div>
        <div>
          <dt>Valor a faturar ao Cliente</dt>
          <dd>{moeda2(eco.aFaturar)}</dd>
        </div>
        <div>
          <dt>Frete</dt>
          <dd>
            {c.frete.modalidade} · {PAGADORES[c.frete.pagador]} · {moeda2(c.frete.valor)}
          </dd>
        </div>
        <div>
          <dt>Frete alocado às Ordens</dt>
          <dd data-teste="frete-alocado-pedido">
            {moeda2(p.freteAlocado)} de {moeda2(c.frete.modalidade === 'CIF' ? 0 : c.frete.valor)}
          </dd>
        </div>
        <div>
          <dt>Condições</dt>
          <dd>{[pagamentoTexto(c.condicoes), c.condicoes.prazoGeral].filter((x) => x && x !== '—').join(' · ') || '—'}</dd>
        </div>
      </dl>
    </details>
  );
}

/** Itens do Pedido. Venda (pacote 4): colunas da referência aprovada; Locação e Trade-in mantêm as próprias. */
function ItensGanhos({ pedido: p }: { pedido: PedidoCompleto }) {
  const c = p.conteudo;
  if (c.locacao)
    return (
      <section data-teste="itens-ganhos-pedido">
        <h2>Itens ganhos — componentes do projeto</h2>
        <div className="rolagem">
          <table className="tabela tabela-compacta" data-teste="componentes-pedido">
            <thead>
              <tr>
                <th>PN/SKU</th>
                <th>Descrição</th>
                <th>Fabricante</th>
                <th>Pilar</th>
                <th>Tipo</th>
                <th>Qtd ganha</th>
                {c.locacao.componentes.some((x) => x.observacaoTecnica) && <th>Observação técnica</th>}
              </tr>
            </thead>
            <tbody>
              {c.locacao.componentes.map((x) => (
                <tr key={x.id}>
                  <td>{x.pn || '—'}</td>
                  <td>{x.descricao}</td>
                  <td>{x.fabricante || '—'}</td>
                  <td>{x.pilar || '—'}</td>
                  <td>{x.tipoItem || '—'}</td>
                  <td>{x.quantidadeAceita}</td>
                  {c.locacao!.componentes.some((y) => y.observacaoTecnica) && <td>{x.observacaoTecnica || '—'}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    );
  if (c.tradein) {
    const T = c.tradein;
    // Pacote 4 (bloco 2): mesmo padrão da tabela da Venda; o parceiro é o comprador da avaliação aceita
    const comprador = p.entidades[c.itens[0]?.entidadeId ?? ''];
    return (
      <section data-teste="itens-ganhos-pedido">
        <h2>Itens / lotes ({T.linhas.length})</h2>
        <RolagemIndicada className="rolagem quadro-tabela primeira-fixa" teste="linhas-tradein">
          <table className="tabela tabela-compacta tabela-itens-pedido" data-teste="linhas-tradein">
            <thead>
              <tr>
                <th>Equipamento / lote</th>
                <th>Qtd</th>
                <th>Parceiro</th>
                <th>Avaliação Unit.</th>
                <th>Avaliação Total</th>
                <th>Valor Unit. ao Cliente</th>
                <th>Valor Total ao Cliente</th>
                <th>Margem R$</th>
                <th>Margem %</th>
              </tr>
            </thead>
            <tbody>
              {T.linhas.map((l) => {
                const e = T.equipamentos.find((x) => x.id === l.id);
                // o nome já traz categoria — fabricante modelo quando não há descrição própria; o lote vem embaixo
                const detalhe = e ? [e.descricao ? [e.categoria, [e.fabricante, e.modelo].filter(Boolean).join(' ')].filter(Boolean).join(' — ') : '', e.lote].filter(Boolean).join(' · ') : '';
                return (
                  <tr key={l.id} data-teste={`lote-pedido-${l.id}`}>
                    <td>
                      <span className="duas-linhas" title={[l.descricao, detalhe].filter(Boolean).join(' — ')}>
                        {l.descricao}
                      </span>
                      {detalhe && detalhe !== l.descricao ? <div className="texto-secundario">{detalhe}</div> : null}
                    </td>
                    <td className="numero">{l.quantidade}</td>
                    <td>
                      {comprador ? (
                        <span className="duas-linhas" title={`${comprador.razao} — CNPJ ${comprador.cnpj}`} data-teste={`parceiro-lote-${l.id}`}>
                          {comprador.grupo}
                        </span>
                      ) : (
                        <span className="selo selo-alerta">comprador não identificado</span>
                      )}
                    </td>
                    <td className="numero nowrap">{moeda2(l.unitBruto)}</td>
                    <td className="numero nowrap">{moeda2(l.totalBruto)}</td>
                    <td className="numero nowrap">{moeda2(l.unitCliente)}</td>
                    <td className="numero nowrap">{moeda2(l.totalCliente)}</td>
                    <td className="numero nowrap">{moeda2(l.comissao)}</td>
                    <td className="numero nowrap">{pctNum(l.percentual)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </RolagemIndicada>
      </section>
    );
  }
  return (
    <section data-teste="itens-ganhos-pedido">
      <h2>Itens ({c.itens.length})</h2>
      <RolagemIndicada className="rolagem quadro-tabela primeira-fixa" teste="itens-pedido">
        <table className="tabela tabela-compacta tabela-itens-pedido" data-teste="itens-pedido">
          <thead>
            <tr>
              <th>PN</th>
              <th>Descrição</th>
              <th>Qtd</th>
              <th>Parceiro</th>
              <th>Custo Unit.</th>
              <th>Custo Total</th>
              <th>Venda Unit.</th>
              <th>Venda Total</th>
              <th>Margem R$</th>
              <th>Margem %</th>
            </tr>
          </thead>
          <tbody>
            {c.itens.map((i) => {
              const e = i.entidadeId ? p.entidades[i.entidadeId] : null;
              const venda = i.precoUnit * i.quantidade;
              const m = (i.precoUnit - i.custoUnit) * i.quantidade;
              const nome = i.produtoCopia?.nome ?? '—';
              return (
                <tr key={i.itemId} data-teste={`item-pedido-${i.itemId}`}>
                  <td className="nowrap">{i.produtoCopia?.sku || '—'}</td>
                  <td>
                    <span className="duas-linhas" title={nome}>
                      {nome}
                    </span>
                  </td>
                  <td className="numero" title={`Em Ordens: ${p.emOrdens[i.itemId] ?? 0} · faturado: ${p.faturadoPorItem[i.itemId] ?? 0}`}>
                    {i.quantidade}
                  </td>
                  <td>
                    {e ? (
                      <span className="duas-linhas" title={`${e.razao} — CNPJ ${e.cnpj}`} data-teste={`parceiro-item-${i.itemId}`}>
                        {e.grupo}
                      </span>
                    ) : (
                      <span className="selo selo-alerta">sem parceiro faturador</span>
                    )}
                  </td>
                  <td className="numero nowrap">{moeda2(i.custoUnit)}</td>
                  <td className="numero nowrap">{moeda2(i.custoUnit * i.quantidade)}</td>
                  <td className="numero nowrap">{moeda2(i.precoUnit)}</td>
                  <td className="numero nowrap">{moeda2(venda)}</td>
                  <td className="numero nowrap">{moeda2(m)}</td>
                  <td className="numero nowrap">{pct(venda ? m / venda : 0)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </RolagemIndicada>
      {p.naoGanhos.length > 0 && (
        <p className="texto-secundario" data-teste="itens-nao-ganhos">
          Itens da versão aceita que NÃO foram ganhos (não entram no Pedido nem geram Ordem): {p.naoGanhos.join(', ')}.
        </p>
      )}
    </section>
  );
}

/** Pacote 4: quadro de local (Faturamento/Entrega) com as linhas alinhadas; a procedência fica no Histórico. */
function BlocoLocal({ titulo, l, teste, marca }: { titulo: string; l: LocalCongelado; teste: string; marca?: string }) {
  return (
    <section className="cartao-entidade cartao-local" data-teste={teste}>
      <div className="cab-local">
        <h3>{titulo}</h3>
        {marca && (
          <span className="selo selo-neutro" data-teste={`${teste}-marca`}>
            {marca}
          </span>
        )}
      </div>
      <dl className="dados">
        <dt>CNPJ</dt>
        <dd>{l.cnpj}</dd>
        <dt>Razão social</dt>
        <dd>{l.razao}</dd>
        <dt>Endereço</dt>
        <dd>{enderecoTela(l.endereco)}</dd>
        <dt>Contato</dt>
        <dd>{[l.contato, l.telefone].filter(Boolean).join(' · ') || '—'}</dd>
      </dl>
    </section>
  );
}

/** Faturamento e Entrega lado a lado, simétricos (empilham em telas menores); Trade-in mostra o local do parque congelado. */
function FaturamentoEntregaPedido({ pedido: p }: { pedido: PedidoCompleto }) {
  const c = p.conteudo;
  const mesmo = c.entrega.origem === 'mesmo endereço do faturamento';
  return (
    <div className="grade-2 blocos-endereco blocos-endereco-pedido" data-teste="faturamento-entrega-pedido">
      <BlocoLocal titulo={c.tradein ? 'Cliente (entidade/CNPJ)' : 'Faturamento'} l={c.faturamento} teste="pedido-faturamento" />
      {c.tradein ? (
        <section className="cartao-entidade cartao-local" data-teste="pedido-entrega">
          <div className="cab-local">
            <h3>Local do parque</h3>
          </div>
          <dl className="dados">
            <dt>Endereço</dt>
            <dd>{c.tradein.localCongelado?.texto ?? c.tradein.localParque}</dd>
            <dt>Contato</dt>
            <dd>{c.tradein.contatoLocal ? `${c.tradein.contatoLocal.nome} · ${c.tradein.contatoLocal.telefone}` : '—'}</dd>
          </dl>
        </section>
      ) : (
        <BlocoLocal titulo={c.locacao ? 'Entrega e instalação' : 'Entrega'} l={c.entrega} teste="pedido-entrega" marca={mesmo ? 'Mesmo endereço de faturamento' : undefined} />
      )}
    </div>
  );
}

/** Pacote 4: Histórico do Pedido — o que saiu da leitura principal continua acessível (congelamento, cadeia, procedência). */
function ModalHistoricoPedido({ pedido: p, aoFechar }: { pedido: PedidoCompleto; aoFechar: () => void }) {
  const c = p.conteudo;
  const componentes = (c.locacao?.componentes ?? c.tradein?.equipamentos ?? []).length;
  return (
    <Modal
      titulo={`Histórico do ${p.numero}`}
      aoFechar={aoFechar}
      largo
      rodape={
        <button type="button" className="botao-secundario" onClick={aoFechar} data-teste="fechar-historico">
          Fechar
        </button>
      }
    >
      <div className="historico-pedido" data-teste="historico-pedido-conteudo">
        <p className="sem-margem" data-teste="tipo-congelado">
          🔒 Tipo {TIPOS[p.tipo]} copiado da Oportunidade #{p.oportunidade.numero} em {dataBr(p.tipoCopiadoEm)} (congelado).
        </p>
        <p data-teste="versao-aceita">
          🔒 Congelado da versão aceita (Proposta {p.proposta.numero} — v{p.versao}). {p.tipo === 'venda' ? `${c.itens.length} de ${p.linhasVersao} item(ns) ganho(s)` : `${componentes} de ${p.linhasVersao} componente(s) ganho(s) — conjunto`}: itens, quantidades, custos, preços, margens, frete e condições foram copiados sem redigitação. Alterações só por nova versão da Proposta ou “Reabrir para correção” (retificação auditada, com histórico).
        </p>
        <dl className="pares-detalhes">
          <div>
            <dt>Gerado</dt>
            <dd>
              {dataBr(p.geradoEm.slice(0, 10))} por {p.geradoPor}
            </dd>
          </div>
          <div>
            <dt>Fechamento</dt>
            <dd>{p.dataFechamento ? dataBr(p.dataFechamento) : '—'}</dd>
          </div>
          <div data-teste="origem-enderecos">
            <dt>Procedência dos endereços</dt>
            <dd>
              Faturamento: {c.faturamento.origem}
              {!c.tradein && <> · Entrega: {c.entrega.origem}</>}
              {c.tradein && <> · Local do parque: {c.tradein.localCongelado?.origem ?? 'texto livre'}</>}
            </dd>
          </div>
        </dl>
        <CadeiaRastreabilidade pedido={p} atual="pedido" />
      </div>
    </Modal>
  );
}

const paraBase64 = (b: Uint8Array) => {
  let s = '';
  for (let i = 0; i < b.length; i += 0x8000) s += String.fromCharCode(...b.subarray(i, i + 0x8000));
  return btoa(s);
};
/** Arquivo escolhido na tela → corpo da API (base64; o servidor confere tipo pelo conteúdo e tamanho). */
export async function arquivoParaApi(f: File | null) {
  return f ? { nome: f.name, conteudo_base64: paraBase64(new Uint8Array(await f.arrayBuffer())) } : undefined;
}
export const ACEITE_ARQUIVO_OC = '.pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.eml,.msg';

async function baixarArquivoOc(numeroPedido: string, oc: OcCliente) {
  const a = await api.get<{ nome: string; tipo: string; conteudo_base64: string }>(`/api/pedidos/${numeroPedido}/ocs/${oc.id}/arquivo`);
  const bytes = Uint8Array.from(atob(a.conteudo_base64), (x) => x.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes as BlobPart]));
  const el = document.createElement('a');
  el.href = url;
  el.download = a.nome;
  el.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Pacote 4: escolha EXPLÍCITA do que a OC abrange — o Pedido inteiro ou itens escolhidos (nada é suposto). */
export function AbrangenciaOc({ abrangencia, setAbrangencia, itens, escolhidos, setEscolhidos }: { abrangencia: '' | 'pedido' | 'itens'; setAbrangencia: (a: 'pedido' | 'itens') => void; itens: { id: string; nome: string }[]; escolhidos: string[]; setEscolhidos: (x: string[]) => void }) {
  return (
    <fieldset className="largura-total abrangencia-oc" data-teste="oc-abrangencia">
      <legend>
        A OC abrange
        <Ajuda titulo="Abrangência da OC">A OC do Cliente vale para o Pedido inteiro ou só para os itens escolhidos. A escolha é obrigatória e fica registrada; para mudar depois, use a substituição (o histórico é preservado).</Ajuda>
      </legend>
      <label className="opcao">
        <input type="radio" name="abrangencia-oc" checked={abrangencia === 'pedido'} onChange={() => setAbrangencia('pedido')} data-teste="oc-abrangencia-pedido" /> O Pedido inteiro
      </label>
      <label className="opcao">
        <input type="radio" name="abrangencia-oc" checked={abrangencia === 'itens'} onChange={() => setAbrangencia('itens')} data-teste="oc-abrangencia-itens" /> Itens escolhidos
      </label>
      {abrangencia === 'itens' && (
        <div className="itens-abrangencia">
          {itens.map((i) => (
            <label key={i.id} className="opcao">
              <input type="checkbox" checked={escolhidos.includes(i.id)} onChange={(e) => setEscolhidos(e.target.checked ? [...escolhidos, i.id] : escolhidos.filter((x) => x !== i.id))} data-teste={`oc-item-${i.id}`} /> {i.nome}
            </label>
          ))}
        </div>
      )}
    </fieldset>
  );
}

/** Itens do Pedido que uma OC pode abranger, com o nome para a tela. */
const itensParaOc = (c: ConteudoPedido) => (c.locacao ? c.locacao.componentes.map((x) => ({ id: x.id, nome: x.descricao })) : c.tradein ? c.tradein.equipamentos.map((x) => ({ id: x.id, nome: x.descricao })) : c.itens.map((i) => ({ id: i.itemId, nome: `${i.produtoCopia?.sku ? `${i.produtoCopia.sku} — ` : ''}${i.produtoCopia?.nome ?? i.itemId}` })));
const declaracaoAtiva = (p: PedidoCompleto) => (p.ocDeclaracoes ?? []).find((d) => !d.encerradaEm) ?? null;
/** OCs vigentes que abrangem algum dos itens (o vínculo gravado é lido como está). */
const ocsDosItens = (p: PedidoCompleto, itens: string[]) => p.ocs.filter((o) => o.situacao === 'Vigente' && o.itens.some((i) => itens.includes(i)));
const textoAbrangencia = (p: PedidoCompleto, o: OcCliente) => {
  const nomes = itensParaOc(p.conteudo);
  if (o.abrangencia === 'pedido') return 'O Pedido inteiro';
  const lista = o.itens.map((i) => nomes.find((n) => n.id === i)?.nome ?? i).join('; ');
  if (o.abrangencia === 'itens') return `Itens: ${lista}`;
  return o.itens.length ? `Registro anterior — itens vinculados: ${lista}` : 'Registro anterior — sem vínculo de itens';
};

/** Pacote 4: célula "OC do Cliente" de uma Ordem — OCs que abrangem os seus itens (todas consultáveis); senão, a situação. */
function CelulaOc({ pedido: p, itens, teste, abrir }: { pedido: PedidoCompleto; itens: string[]; teste: string; abrir: (ocId?: string) => void }) {
  const ocs = ocsDosItens(p, itens);
  if (ocs.length)
    return (
      <span className="ocs-celula" data-teste={teste}>
        {ocs.map((o) => (
          <button key={o.id} type="button" className="botao-link" onClick={() => abrir(o.id)} title={textoAbrangencia(p, o)} data-teste={`${teste}-${o.numero}`}>
            {o.arquivo && (
              <span aria-label="com arquivo" data-teste={`${teste}-${o.numero}-clipe`}>
                📎{' '}
              </span>
            )}
            {o.numero}
          </button>
        ))}
      </span>
    );
  if (declaracaoAtiva(p))
    return (
      <button type="button" className="botao-link texto-secundario" onClick={() => abrir()} data-teste={teste}>
        Não se aplica
      </button>
    );
  return (
    <button type="button" className="botao-link texto-secundario" onClick={() => abrir()} data-teste={teste}>
      Não informada
    </button>
  );
}

/**
 * Pacote 4: consulta e gerenciamento da OC do Cliente (abre pela coluna da Ordem ou pelo resumo): arquivo, itens
 * abrangidos, histórico, incluir/substituir e declarar "Não se aplica", conforme permissões. Nada histórico é editado.
 */
function ModalOcCliente({ pedido: p, foco, aoFechar, aoMudar }: { pedido: PedidoCompleto; foco?: string; aoFechar: () => void; aoMudar: (novo: PedidoCompleto) => void }) {
  const { pode } = useSessao();
  const [modo, setModo] = useState<'lista' | 'vincular' | 'declarar'>('lista');
  const [substitui, setSubstitui] = useState<string | undefined>();
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  // formulário da OC
  const anterior = p.ocs.find((x) => x.id === substitui);
  const [numero, setNumero] = useState('');
  const [dataRecebimento, setData] = useState(hojeIso());
  const [observacao, setObservacao] = useState('');
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [tipo, setTipo] = useState<TipoOc>(p.ocs.length ? 'complementar' : 'inicial');
  const [justificativa, setJustificativa] = useState('');
  const [abrangencia, setAbrangencia] = useState<'' | 'pedido' | 'itens'>('');
  const [escolhidos, setEscolhidos] = useState<string[]>([]);
  const [tentou, setTentou] = useState(false);
  const ativa = declaracaoAtiva(p);
  const vigentes = p.ocs.filter((o) => o.situacao === 'Vigente');
  const podeVincular = pode('vincular_oc', 'pedido') && !p.cancelamento;
  const faltam =
    modo === 'declarar'
      ? [justificativa.trim().length < 4 && 'Informe a justificativa (por que a OC do Cliente não se aplica a este Pedido).']
      : [!numero.trim() && 'Informe o número da OC.', !dataRecebimento && 'Informe a data de recebimento.', !abrangencia && 'Informe o que a OC abrange: o Pedido inteiro ou os itens.', abrangencia === 'itens' && escolhidos.length === 0 && 'Escolha ao menos um item.', anterior && !justificativa.trim() && 'Substituir exige justificativa (o histórico é preservado).'];
  const pendentes = faltam.filter(Boolean) as string[];
  const abrirForm = (m: 'vincular' | 'declarar', sub?: string) => {
    setModo(m);
    setSubstitui(sub);
    setTipo(sub ? 'substituta' : p.ocs.length ? 'complementar' : 'inicial');
    setNumero('');
    setJustificativa('');
    setAbrangencia('');
    setEscolhidos([]);
    setArquivo(null);
    setTentou(false);
    setErro('');
  };
  const salvar = async () => {
    setTentou(true);
    if (pendentes.length) return;
    setErro('');
    setSalvando(true);
    try {
      if (modo === 'declarar') aoMudar(await api.post<PedidoCompleto>(`/api/pedidos/${p.numero}/oc-nao-se-aplica`, { justificativa }));
      else {
        const corpo = { numero, data_recebimento: dataRecebimento, observacao, arquivo: await arquivoParaApi(arquivo), abrangencia, ...(abrangencia === 'itens' ? { itens: escolhidos } : {}), ...(anterior ? { substitui_id: anterior.id, justificativa } : { tipo }) };
        aoMudar(await api.post<PedidoCompleto>(`/api/pedidos/${p.numero}/ocs`, corpo));
      }
      setModo('lista');
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  };
  const rodape =
    modo === 'lista' ? (
      <button type="button" className="botao-secundario" onClick={aoFechar} data-teste="fechar-oc">
        Fechar
      </button>
    ) : (
      <>
        <button type="button" className="botao-secundario" onClick={() => setModo('lista')} data-teste="voltar-oc">
          Voltar
        </button>
        <button type="button" className="botao-primario" onClick={() => void salvar()} disabled={salvando} data-teste={modo === 'declarar' ? 'salvar-nao-se-aplica' : 'salvar-oc'}>
          {modo === 'declarar' ? 'Registrar “Não se aplica”' : anterior ? 'Substituir (mantém o histórico)' : 'Vincular OC'}
        </button>
      </>
    );
  return (
    <Modal titulo={modo === 'declarar' ? `OC do Cliente não se aplica · ${p.numero}` : anterior ? `Substituir a OC ${anterior.numero}` : modo === 'vincular' ? `Vincular OC do Cliente · ${p.numero}` : `OC do Cliente · ${p.numero}`} aoFechar={aoFechar} largo rodape={rodape}>
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      {modo === 'lista' && (
        <div data-teste="oc-cliente">
          <div className="barra-titulo">
            <p className="sem-margem" data-teste="situacao-oc">
              {vigentes.length ? `${vigentes.length} OC vigente(s)` : ativa ? '“Não se aplica” declarado' : 'Não informada'}
              <Ajuda titulo="OC do Cliente">Documento recebido do Cliente (não é a Ordem de Compra ao Fornecedor). Cada Ordem mostra as OCs que abrangem os seus itens. “Não informada” ainda permite incluir; “Não se aplica” é uma declaração expressa, com justificativa.</Ajuda>
            </p>
            {podeVincular && (
              <div className="acoes-direita">
                {!vigentes.length && !ativa && (
                  <button type="button" className="botao-secundario" onClick={() => abrirForm('declarar')} data-teste="declarar-nao-se-aplica">
                    Declarar “Não se aplica”
                  </button>
                )}
                <button type="button" className={p.ocs.length ? 'botao-secundario' : 'botao-primario'} onClick={() => abrirForm('vincular')} data-teste="vincular-oc">
                  {p.ocs.length ? 'Adicionar OC (complementar/revisada)' : 'Vincular OC'}
                </button>
              </div>
            )}
          </div>
          {p.ocs.length === 0 ? (
            <Estado tipo={ativa ? 'concluido' : 'bloqueado'} titulo={ativa ? 'OC do Cliente: não se aplica (declarado)' : 'OC do Cliente não informada — o Cliente ainda não enviou'} itens={[ativa ? 'Se a OC chegar, registre-a normalmente: a declaração fica no histórico.' : 'Use “Vincular OC” quando o documento chegar, ou declare “Não se aplica” com justificativa.']} teste="oc-pendente-pedido" />
          ) : (
            <div className="rolagem">
              <table className="tabela tabela-compacta" data-teste="lista-ocs">
                <thead>
                  <tr>
                    <th>Número</th>
                    <th>Tipo</th>
                    <th>Recebimento</th>
                    <th>Arquivo</th>
                    <th>Abrange</th>
                    <th>Status</th>
                    <th>Registrada por</th>
                    <th>Histórico</th>
                    <th className="col-acoes">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {p.ocs.map((x) => {
                    const substituta = p.ocs.find((y) => y.id === x.substituidaPor);
                    const substituiu = p.ocs.find((y) => y.id === x.substituiId);
                    const historico = [`${dataBr(x.registradaEm.slice(0, 10))} · OC ${x.numero} (${x.tipo.toLowerCase()}) registrada ${x.arquivo ? `com o arquivo ${x.arquivo.nome}` : 'sem arquivo'}${substituiu ? `, substituindo a ${substituiu.numero}` : ''} (${x.usuario})`, ...(substituta ? [`${dataBr(substituta.registradaEm.slice(0, 10))} · Substituída pela OC ${substituta.numero}: ${x.motivoSubstituicao} (${substituta.usuario})`] : [])];
                    return (
                      <tr key={x.id} className={`${x.situacao === 'Substituída' ? 'linha-historico' : ''}${x.id === foco ? ' linha-foco' : ''}`} data-teste={`oc-${x.numero}`}>
                        <td>{x.numero}</td>
                        <td>{x.tipo}</td>
                        <td className="nowrap">{x.dataRecebimento ? dataBr(x.dataRecebimento) : <span className="texto-secundario" data-teste="oc-data-nao-informada">não informada na origem</span>}</td>
                        <td>
                          {x.arquivo ? (
                            <button type="button" className="botao-link" onClick={() => baixarArquivoOc(p.numero, x).catch((e: Error) => setErro(e.message))} data-teste={`arquivo-oc-${x.numero}`}>
                              📎 {x.arquivo.nome}
                            </button>
                          ) : (
                            <span className="texto-secundario">sem arquivo</span>
                          )}
                        </td>
                        <td data-teste={`abrangencia-oc-${x.numero}`}>{textoAbrangencia(p, x)}</td>
                        <td>{x.situacao === 'Substituída' ? <span className="rotulo-substituida">Substituída</span> : <span className="selo selo-confirmado">Vigente</span>}</td>
                        <td>
                          {x.usuario}
                          <div className="texto-secundario">
                            {dataBr(x.registradaEm.slice(0, 10))} às {new Date(x.registradaEm).toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </td>
                        <td>
                          <details>
                            <summary>{historico.length} registro(s)</summary>
                            <ul className="lista-simples">
                              {historico.map((h, i) => (
                                <li key={i}>{h}</li>
                              ))}
                            </ul>
                          </details>
                        </td>
                        <td className="col-acoes">
                          {x.situacao === 'Vigente' && pode('substituir_oc', 'pedido') && !p.cancelamento && (
                            <button type="button" className="botao-secundario pequeno" onClick={() => abrirForm('vincular', x.id)} data-teste={`substituir-oc-${x.numero}`}>
                              Substituir 🔒
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {(p.ocDeclaracoes ?? []).length > 0 && (
            <ul className="lista-simples" data-teste="oc-declaracoes">
              {(p.ocDeclaracoes ?? []).map((d) => (
                <li key={d.id} className={d.encerradaEm ? 'texto-secundario' : undefined}>
                  “Não se aplica” declarado por {d.usuario} em {dataBr(String(d.em).slice(0, 10))} às {new Date(d.em).toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' })}: {d.justificativa}
                  {d.encerradaEm && ` — encerrado pela OC ${d.encerradaPor} em ${dataBr(String(d.encerradaEm).slice(0, 10))}`}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {modo === 'declarar' && (
        <div className="grade-form sem-borda" data-teste="form-nao-se-aplica">
          <p className="largura-total sem-margem texto-secundario">Declaração expressa para o Pedido inteiro, com autor, data e hora. Se a OC chegar depois, ela é registrada normalmente e esta declaração fica no histórico.</p>
          <label className="largura-total">
            Justificativa (obrigatória)
            <textarea value={justificativa} onChange={(e) => setJustificativa(e.target.value)} maxLength={1000} rows={3} data-teste="justificativa-nao-se-aplica" />
          </label>
        </div>
      )}
      {modo === 'vincular' && (
        <>
          {anterior && <p className="aviso aviso-info">A OC {anterior.numero} fica como “Substituída”, preservada no histórico. Nada é alterado em silêncio.</p>}
          {ativa && !anterior && <p className="aviso aviso-info">Este Pedido tem “Não se aplica” declarado: ao registrar a OC, a declaração é encerrada e fica no histórico.</p>}
          <div className="grade-form sem-borda">
            <label>
              Número da OC
              <input value={numero} onChange={(e) => setNumero(e.target.value)} maxLength={80} data-teste="oc-numero-pedido" />
            </label>
            <label>
              Tipo
              <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoOc)} disabled={Boolean(anterior)} data-teste="oc-tipo">
                {TIPOS_OC.filter((x) => (anterior ? x === 'substituta' : x !== 'substituta' && (p.ocs.length ? x !== 'inicial' : true))).map((x) => (
                  <option key={x} value={x}>
                    {NOMES_TIPO_OC[x]}
                  </option>
                ))}
              </select>
            </label>
            <CampoData rotulo="Data de recebimento" valor={dataRecebimento} aoMudar={setData} max={hojeIso()} teste="oc-data-pedido" obrigatorio />
            <label>
              Arquivo (PDF recomendado)
              <input type="file" accept={ACEITE_ARQUIVO_OC} onChange={(e) => setArquivo(e.target.files?.[0] ?? null)} data-teste="oc-arquivo-pedido" />
            </label>
            <label className="largura-total">
              Observação
              <input value={observacao} onChange={(e) => setObservacao(e.target.value)} />
            </label>
            {anterior && (
              <label className="largura-total">
                Justificativa da substituição (obrigatória)
                <input value={justificativa} onChange={(e) => setJustificativa(e.target.value)} data-teste="oc-justificativa" />
              </label>
            )}
            <AbrangenciaOc abrangencia={abrangencia} setAbrangencia={setAbrangencia} itens={itensParaOc(p.conteudo)} escolhidos={escolhidos} setEscolhidos={setEscolhidos} />
          </div>
        </>
      )}
      {modo !== 'lista' && tentou && pendentes.length > 0 && <Estado tipo="bloqueado" titulo="Falta completar" itens={pendentes} teste="faltam-oc" />}
    </Modal>
  );
}

/** Etapa de ALOCAÇÃO DO FRETE antes de gerar as Ordens: a soma precisa fechar exatamente com o total ainda não alocado. */
function AlocacaoFrete({ pedido: p, aloc, setAloc }: { pedido: PedidoCompleto; aloc: Alocacao; setAloc: (a: Alocacao) => void }) {
  const c = p.conteudo;
  const restante = p.freteAAlocar;
  const conf = conferirAlocacao(restante, aloc);
  const previa = p.previaOrdens;
  if (c.frete.modalidade === 'CIF' || restante === 0) return null;
  const sugerida = previa.find((x) => x.entidadeId === c.frete.entidadeId);
  return (
    <fieldset className="alocacao-frete" data-teste="alocacao-frete">
      <legend>
        Alocação do frete ({c.frete.modalidade} · {PAGADORES[c.frete.pagador]})
      </legend>
      {previa.length === 1 ? (
        <p className="texto-secundario">Uma só Ordem: o frete vai integral para ela.</p>
      ) : sugerida ? (
        <p className="texto-secundario" data-teste="sugestao-frete">
          Sugestão: o frete foi cotado com {sugerida.entidade?.fantasia} — alocado integralmente a ela. Você pode alterar.
        </p>
      ) : (
        <p className="texto-secundario">Informe quanto do frete vai para cada Ordem. Não há rateio automático.</p>
      )}
      <div className="grade-alocacao">
        {previa.map((x) => (
          <label key={x.entidadeId}>
            {x.entidade?.fantasia} ({x.entidade?.cnpj})
            <input type="number" min={0} step="0.01" value={aloc[x.entidadeId] ?? 0} onChange={(ev) => setAloc({ ...aloc, [x.entidadeId]: Number(ev.target.value) })} aria-label={`Frete da Ordem de ${x.entidade?.fantasia}`} data-teste={`alocacao-${x.entidadeId}`} />
          </label>
        ))}
      </div>
      <ResumoIndicadores
        compacto
        teste="totais-alocacao"
        itens={[
          { rotulo: 'Total do frete', valor: moeda2(conf.total) },
          { rotulo: 'Total alocado', valor: moeda2(conf.alocado) },
          { rotulo: 'Diferença', valor: <span className={conf.ok ? undefined : 'diferenca-aberta'}>{moeda2(conf.diferenca)}</span> },
        ]}
      />
      {!conf.ok && (
        <p className="aviso aviso-alerta" role="alert" data-teste="aviso-alocacao">
          {conf.diferenca > 0 ? `Faltam ${moeda2(conf.diferenca)} para fechar o frete.` : conf.diferenca < 0 ? `A alocação passa ${moeda2(-conf.diferenca)} do frete total.` : 'Valores negativos não são aceitos.'} As Ordens só podem ser geradas com diferença zero.
        </p>
      )}
      <p className="texto-secundario">{c.frete.pagador === 'inttrix' ? 'FOB absorvido pela Inttrix: a parcela de cada Ordem reduz a comissão daquela Ordem.' : 'FOB pago pelo cliente: a parcela de cada Ordem compõe o valor a faturar daquela Ordem.'}</p>
    </fieldset>
  );
}

/**
 * Pacote 4 — "Conferir OCF" antes de gerar (modal): um agrupamento por entidade faturadora, todos visíveis antes de
 * confirmar; custos e vendas só para conferência interna (o PDF ao fornecedor não muda). "Voltar sem gerar" não grava
 * nada. A confirmação envia a revisão e os agrupamentos vistos: se o Pedido mudou, o servidor recusa sem gerar.
 */
function ModalConferirOcf({ pedido: p, aoFechar, aoGerar }: { pedido: PedidoCompleto; aoFechar: () => void; aoGerar: () => void }) {
  const c = p.conteudo;
  const [conferido, setConferido] = useState(false);
  const [alocacao, setAlocacao] = useState<Alocacao | null>(null);
  const [erro, setErro] = useState('');
  const [gerando, setGerando] = useState(false);
  const aloc = alocacao ?? p.alocacaoSugerida;
  const conf = conferirAlocacao(p.freteAAlocar, aloc);
  const n = p.previaOrdens.length;
  const gerar = async () => {
    setErro('');
    setGerando(true);
    try {
      await api.post<{ geradas: string[] }>(`/api/pedidos/${p.numero}/ordens`, { conferido: true, alocacao: aloc, revisao: p.revisao ?? 0, previa: p.previaOrdens.map((g) => ({ entidadeId: g.entidadeId, itens: g.itens })) });
      aoGerar();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setGerando(false);
    }
  };
  return (
    <Modal
      titulo="Conferir OCF"
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <label className="opcao conferi-ordem">
            <input type="checkbox" checked={conferido} onChange={(e) => setConferido(e.target.checked)} data-teste="conferido" /> {n > 1 ? `Conferi os dados das ${n} Ordens.` : 'Conferi os dados da Ordem.'}
          </label>
          <button type="button" className="botao-secundario" onClick={aoFechar} data-teste="voltar-sem-gerar">
            Voltar sem gerar
          </button>
          <button type="button" className="botao-primario" disabled={!conferido || !conf.ok || gerando} title={!conf.ok ? 'A alocação do frete precisa fechar exatamente com o total' : !conferido ? 'Marque a conferência' : undefined} onClick={() => void gerar()} data-teste="gerar-ordens">
            {n > 1 ? `Confirmar e gerar ${n} OCF` : 'Confirmar e gerar OCF'}
          </button>
        </>
      }
    >
      <div className="conferir-ocf" data-teste="conferencia-pedido">
        <p className="sem-margem texto-secundario" data-teste="subtitulo-conferir-ocf">
          {p.numero} · {n} OCF a gerar
        </p>
        {erro && (
          <p className="aviso aviso-erro" role="alert" data-teste="erro-conferir-ocf">
            {erro}
          </p>
        )}
        {p.previaOrdens.map((g) => {
          const linhas = g.itens.map((x) => ({ x, i: c.itens.find((y) => y.itemId === x.itemId)! }));
          const custo = linhas.reduce((s, { x, i }) => s + i.custoUnit * x.quantidade, 0);
          const venda = linhas.reduce((s, { x, i }) => s + i.precoUnit * x.quantidade, 0);
          const frete = c.frete.modalidade === 'CIF' ? 0 : (aloc[g.entidadeId] ?? 0);
          return (
            <section key={g.entidadeId} className="grupo-ocf" data-teste={`conferir-ocf-${g.entidadeId}`} aria-label={`OCF para ${g.entidade?.razao ?? ''}`}>
              <div className="grade-2 quadros-ocf">
                <div className="quadro-ocf">
                  <span className="rotulo-campo">Fornecedor</span>
                  <strong>{g.entidade?.razao}</strong>
                  <span>{g.entidade?.cnpj}</span>
                  {g.entidade?.grupo && <span className="selo selo-neutro">{g.entidade.grupo}</span>}
                </div>
                <div className="quadro-ocf">
                  <span className="rotulo-campo">Cliente</span>
                  <strong>{p.cliente.nome}</strong>
                  <span>{p.clienteCnpj}</span>
                </div>
                <div className="quadro-ocf">
                  <span className="rotulo-campo">Faturamento</span>
                  <strong>{c.faturamento.razao}</strong>
                  <span>{c.faturamento.cnpj}</span>
                </div>
                <div className="quadro-ocf" data-teste="conferir-ocf-entrega">
                  <span className="rotulo-campo">Entrega</span>
                  <strong>{c.entrega.razao}</strong>
                  <span>{enderecoTela(c.entrega.endereco)}</span>
                  {(c.entrega.contato || c.entrega.telefone) && <span className="texto-secundario">{[c.entrega.contato, c.entrega.telefone].filter(Boolean).join(' · ')}</span>}
                </div>
              </div>
              <RolagemIndicada className="rolagem quadro-tabela" teste={`itens-ocf-${g.entidadeId}`}>
                <table className="tabela tabela-compacta">
                  <thead>
                    <tr>
                      <th>PN</th>
                      <th>Descrição</th>
                      <th>Qtd.</th>
                      <th>Custo Unit. (R$)</th>
                      <th>Custo Total (R$)</th>
                      <th>
                        Venda Unit. (R$)
                        <Ajuda titulo="Venda na conferência">Vendas e margens aparecem só para a conferência interna. O PDF enviado ao fornecedor não inclui valores de venda nem margem.</Ajuda>
                      </th>
                      <th>Venda Total (R$)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map(({ x, i }) => (
                      <tr key={x.itemId}>
                        <td className="nowrap">{i.produtoCopia?.sku || '—'}</td>
                        <td>{i.produtoCopia?.nome ?? x.itemId}</td>
                        <td className="numero">{x.quantidade}</td>
                        <td className="numero nowrap">{moeda2(i.custoUnit).replace('R$', '').trim()}</td>
                        <td className="numero nowrap">{moeda2(i.custoUnit * x.quantidade).replace('R$', '').trim()}</td>
                        <td className="numero nowrap">{moeda2(i.precoUnit).replace('R$', '').trim()}</td>
                        <td className="numero nowrap">{moeda2(i.precoUnit * x.quantidade).replace('R$', '').trim()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </RolagemIndicada>
              <dl className="totais-ocf" data-teste={`totais-ocf-${g.entidadeId}`}>
                <div>
                  <dt>Frete ({freteCurto(c.frete)})</dt>
                  <dd>{moeda2(frete)}</dd>
                </div>
                <div>
                  <dt>Total da OCF</dt>
                  <dd>
                    <strong>{moeda2(custo)}</strong>
                  </dd>
                </div>
                <div>
                  <dt>Venda ao Cliente</dt>
                  <dd>
                    <strong>{moeda2(venda)}</strong>
                  </dd>
                </div>
              </dl>
            </section>
          );
        })}
        <AlocacaoFrete pedido={p} aloc={aloc} setAloc={setAlocacao} />
      </div>
    </Modal>
  );
}

/**
 * Pacote 4 (bloco 2): conferência antes de gerar a OTI, no padrão do Conferir OCF. Valores e margem são da conferência
 * interna (o documento da OTI não muda). "Voltar sem gerar" não grava nada; a confirmação leva o que foi visto
 * (revisão, comprador, lotes e quantidades) e o servidor recusa se o Pedido tiver mudado.
 */
function ModalConferirOti({ pedido: p, aoFechar }: { pedido: PedidoCompleto; aoFechar: () => void }) {
  const c = p.conteudo;
  const T = c.tradein!;
  const [conferido, setConferido] = useState(false);
  const [erro, setErro] = useState('');
  const [gerando, setGerando] = useState(false);
  const entidadeId = c.itens[0]?.entidadeId ?? '';
  const comprador = p.entidades[entidadeId];
  const conf = T.confirmacaoBancaria;
  const gerar = async () => {
    setErro('');
    setGerando(true);
    try {
      const r = await api.post<{ numero: string }>(`/api/pedidos/${p.numero}/documento-operacional`, { conferido: true, revisao: p.revisao ?? 0, entidadeId, itens: T.equipamentos.map((e) => ({ id: e.id, quantidade: e.quantidadeAceita })) });
      navegar(`/ordens/${r.numero}`);
    } catch (e) {
      setErro((e as Error).message);
      setGerando(false);
    }
  };
  return (
    <Modal
      titulo="Conferir OTI"
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <label className="opcao conferi-ordem">
            <input type="checkbox" checked={conferido} onChange={(e) => setConferido(e.target.checked)} data-teste="conferido-oti" /> Conferi os dados da OTI.
          </label>
          <button type="button" className="botao-secundario" onClick={aoFechar} data-teste="voltar-sem-gerar-oti">
            Voltar sem gerar
          </button>
          <button type="button" className="botao-primario" disabled={!conferido || gerando} title={!conferido ? 'Marque a conferência' : undefined} onClick={() => void gerar()} data-teste="gerar-oti">
            Confirmar e gerar OTI
          </button>
        </>
      }
    >
      <div className="conferir-ocf" data-teste="conferencia-oti">
        <p className="sem-margem texto-secundario" data-teste="subtitulo-conferir-oti">
          {p.numero} · 1 OTI a gerar · gerar não envia nada ao comprador
        </p>
        {erro && (
          <p className="aviso aviso-erro" role="alert" data-teste="erro-conferir-oti">
            {erro}
          </p>
        )}
        <section className="grupo-ocf" aria-label="OTI">
          <div className="grade-2 quadros-ocf">
            <div className="quadro-ocf" data-teste="conferir-oti-comprador">
              <span className="rotulo-campo">Parceiro / comprador</span>
              <strong>{comprador?.razao ?? '—'}</strong>
              <span>{comprador?.cnpj ?? ''}</span>
              {comprador?.grupo && <span className="selo selo-neutro">{comprador.grupo}</span>}
            </div>
            <div className="quadro-ocf">
              <span className="rotulo-campo">Cliente / unidade</span>
              <strong>{c.faturamento.razao}</strong>
              <span>{c.faturamento.cnpj}</span>
            </div>
            <div className="quadro-ocf" data-teste="conferir-oti-local">
              <span className="rotulo-campo">Local do parque</span>
              <strong>{T.localCongelado?.texto ?? T.localParque}</strong>
              {T.contatoLocal && <span className="texto-secundario">{[T.contatoLocal.nome, T.contatoLocal.telefone].filter(Boolean).join(' · ')}</span>}
            </div>
            <div className="quadro-ocf" data-teste="conferir-oti-bancarios">
              <span className="rotulo-campo">Dados bancários do Cliente</span>
              <strong>{conf ? 'Confirmados' : 'Não confirmados'}</strong>
              <span className="texto-secundario">{conf ? `por ${conf.usuario} em ${dataBr(conf.dataHora.slice(0, 10))} · fora do HUB` : 'a emissão da OTI exige a confirmação'}</span>
            </div>
          </div>
          <RolagemIndicada className="rolagem quadro-tabela" teste="lotes-oti">
            <table className="tabela tabela-compacta" data-teste="lotes-oti">
              <thead>
                <tr>
                  <th>Equipamento / lote</th>
                  <th>Qtd</th>
                  <th>Avaliação Unit. (R$)</th>
                  <th>Avaliação Total (R$)</th>
                  <th>
                    Valor Unit. ao Cliente (R$)
                    <Ajuda titulo="Valores na conferência">Valores ao Cliente e margem aparecem só para a conferência interna; o documento da OTI não muda.</Ajuda>
                  </th>
                  <th>Valor Total ao Cliente (R$)</th>
                  <th>Margem (R$)</th>
                </tr>
              </thead>
              <tbody>
                {T.linhas.map((l) => (
                  <tr key={l.id}>
                    <td>
                      {l.descricao}
                      {T.equipamentos.find((e) => e.id === l.id)?.lote ? <div className="texto-secundario">{T.equipamentos.find((e) => e.id === l.id)!.lote}</div> : null}
                    </td>
                    <td className="numero">{l.quantidade}</td>
                    <td className="numero nowrap">{moeda2(l.unitBruto).replace('R$', '').trim()}</td>
                    <td className="numero nowrap">{moeda2(l.totalBruto).replace('R$', '').trim()}</td>
                    <td className="numero nowrap">{moeda2(l.unitCliente).replace('R$', '').trim()}</td>
                    <td className="numero nowrap">{moeda2(l.totalCliente).replace('R$', '').trim()}</td>
                    <td className="numero nowrap">{moeda2(l.comissao).replace('R$', '').trim()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </RolagemIndicada>
          <dl className="totais-ocf" data-teste="totais-oti">
            <div>
              <dt>Avaliação bruta</dt>
              <dd>
                <strong>{moeda2(T.bruto)}</strong>
              </dd>
            </div>
            <div>
              <dt>Valor ao Cliente</dt>
              <dd>
                <strong>{moeda2(T.oferta)}</strong>
              </dd>
            </div>
            <div>
              <dt>Margem bruta</dt>
              <dd>
                <strong>
                  {moeda2(T.comissao)} ({pctNum(T.percentualEquivalente)})
                </strong>
              </dd>
            </div>
          </dl>
        </section>
      </div>
    </Modal>
  );
}

/**
 * Ordens e documentos operacionais do Pedido (OCF, OL, OTI), registros separados do Pedido. Pacote 4: OC do Cliente
 * como coluna (consulta/gerenciamento pelo número); a conferência anterior à geração vira o modal "Conferir OCF".
 */
function OrdensDoPedido({ pedido: p, aoMudar, recarregar }: { pedido: PedidoCompleto; aoMudar: (novo: PedidoCompleto) => void; recarregar: () => void }) {
  const { pode } = useSessao();
  const [erro, setErro] = useState('');
  const [oc, setOc] = useState<{ foco?: string } | null>(null);
  const [conferir, setConferir] = useState(false);
  const [conferirOti, setConferirOti] = useState(false);
  const total = p.ordensDetalhe.length + p.documentosDetalhe.length;
  // Pacote 4 (bloco 2): Trade-in no padrão da OCF — tabela sempre visível, sem coluna de frete, "Gerar OTI" com conferência
  const tradein = p.tipo === 'trade_in';
  const serieOk = p.serieDocumento?.configurada !== false;
  const podeGerarOti = tradein && p.documentosDetalhe.length === 0 && pode('gerar', 'ordem') && !p.cancelamento;
  const tipoDoc = p.tipo === 'locacao' ? 'locacao' : 'trade_in';
  const c = p.conteudo;
  const todosItens = itensParaOc(c).map((x) => x.id);
  const vigentes = p.ocs.filter((o) => o.situacao === 'Vigente');
  const ativa = declaracaoAtiva(p);
  const gerarDocumento = async () => {
    setErro('');
    try {
      navegar(`/ordens/${(await api.post<{ numero: string }>(`/api/pedidos/${p.numero}/documento-operacional`, {})).numero}`);
    } catch (e) {
      setErro((e as Error).message);
    }
  };
  const podeGerarOcf = p.tipo === 'venda' && p.previaOrdens.length > 0 && pode('gerar', 'ordem') && !p.cancelamento;
  return (
    <section className="secao-editor" data-teste="ordens-e-documentos" aria-labelledby="t-ordens-docs">
      <div className="barra-titulo">
        <h2 id="t-ordens-docs">Ordens e documentos</h2>
        <div className="acoes-direita">
          <span className="resumo-oc" data-teste="oc-resumo-pedido">
            OC do Cliente:{' '}
            {tradein && p.documentosDetalhe.length > 0 ? null : vigentes.length ? (
              vigentes.map((o, i) => (
                <span key={o.id}>
                  {i > 0 && ' · '}
                  <button type="button" className="botao-link" onClick={() => setOc({ foco: o.id })}>
                    {o.arquivo ? '📎 ' : ''}
                    {o.numero}
                  </button>
                </span>
              ))
            ) : (
              <span className="texto-secundario">{ativa ? 'Não se aplica' : 'Não informada'}</span>
            )}{' '}
            <button type="button" className="botao-secundario pequeno" onClick={() => setOc({})} data-teste="gerenciar-oc">
              Consultar
            </button>
          </span>
          {p.tipo === 'locacao' && p.documentosDetalhe.length === 0 && pode('gerar', 'ordem') && (
            <button type="button" className="botao-primario" onClick={() => void gerarDocumento()} data-teste="gerar-documento-operacional">
              Gerar {NOME_DOCUMENTO[tipoDoc]} ({p.tipo === 'locacao' ? 'OL-' : 'OTI-'})
            </button>
          )}
        </div>
      </div>
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      {total === 0 && !tradein ? (
        <p className="vazio" data-teste="ordens-vazio">
          Nenhuma Ordem gerada ainda para este Pedido. {p.tipo === 'venda' ? 'As Ordens de Compra ao Fornecedor (OCF) são geradas por “Gerar OCF”, uma por entidade faturadora, depois da conferência.' : `Use “Gerar ${NOME_DOCUMENTO[tipoDoc]}”.`}
        </p>
      ) : (
        <RolagemIndicada className="rolagem quadro-tabela primeira-fixa" teste="ordens-do-pedido">
          <table className="tabela tabela-compacta tabela-ordens-pedido" data-teste="ordens-do-pedido">
            <thead>
              <tr>
                <th>Ordem</th>
                <th>Tipo</th>
                <th>Parceiro</th>
                <th>CNPJ</th>
                <th>OC do Cliente</th>
                <th>Status</th>
                <th>Valor</th>
                {!tradein && <th>Frete</th>}
                <th>Envio</th>
                <th>Cancel./Subst.</th>
                <th className="col-acoes">Ações</th>
              </tr>
            </thead>
            <tbody>
              {p.ordensDetalhe.map((o) => (
                <tr key={o.numero} data-teste={`ordem-pedido-${o.numero}`}>
                  <td className="nowrap">
                    <a href={`#/ordens/${o.numero}`}>{o.numero}</a>
                  </td>
                  <td>OCF</td>
                  <td>
                    <span className="duas-linhas" title={o.entidade ? `${o.entidade.razao} (${o.entidade.grupo})` : ''}>
                      {o.entidade?.grupo ?? '—'}
                    </span>
                  </td>
                  <td className="nowrap">{o.entidade?.cnpj ?? '—'}</td>
                  <td>
                    <CelulaOc pedido={p} itens={o.itens ?? []} teste={`oc-ordem-${o.numero}`} abrir={(foco) => setOc({ foco })} />
                  </td>
                  <td>
                    <SeloSituacao texto={ESTADOS_ORDEM[o.estado]} tom={tomOrdem(o.estado)} />
                    <div className="texto-secundario">
                      {o.progresso.faturada} de {o.progresso.total} faturadas
                    </div>
                  </td>
                  <td className="numero nowrap">{moeda2(o.custo)}</td>
                  <td className="nowrap">
                    {freteCurto(c.frete)}
                    {o.frete > 0 && <div className="texto-secundario">{moeda2(o.frete)}</div>}
                  </td>
                  <td className="nowrap">{o.enviadaEm ? dataBr(String(o.enviadaEm).slice(0, 10)) : '—'}</td>
                  <td>{o.substituta ? <span className="rotulo-substituida">Substituída pela {o.substituta}</span> : o.origem ? <span className="texto-secundario">Substituta da {o.origem}</span> : o.estado === 'cancelada' ? 'Cancelada' : '—'}</td>
                  <td className="col-acoes nowrap">
                    <a className="botao-secundario pequeno" href={`#/ordens/${o.numero}`}>
                      Abrir
                    </a>{' '}
                    <a className="botao-secundario pequeno" href={`#/ordens/${o.numero}/pdf`}>
                      PDF
                    </a>
                  </td>
                </tr>
              ))}
              {p.documentosDetalhe.map((x) => {
                const e = p.entidades[c.itens[0]?.entidadeId ?? ''];
                const valor = c.locacao ? moeda2(c.locacao.totalContrato) : moeda2(c.tradein?.oferta ?? 0);
                return (
                  <tr key={x.numero} data-teste={`ordem-pedido-${x.numero}`}>
                    <td className="nowrap">
                      <a href={`#/ordens/${x.numero}`}>{x.numero}</a>
                    </td>
                    <td>{x.tipo === 'locacao' ? 'OL' : 'OTI'}</td>
                    <td>
                      <span className="duas-linhas" title={e ? `${e.razao} (${e.grupo})` : ''}>
                        {e?.grupo ?? '—'}
                      </span>
                    </td>
                    <td className="nowrap">{e?.cnpj ?? '—'}</td>
                    <td>
                      <CelulaOc pedido={p} itens={todosItens} teste={`oc-ordem-${x.numero}`} abrir={(foco) => setOc({ foco })} />
                    </td>
                    <td>
                      <SeloSituacao texto={x.situacao} tom={x.situacao === 'Emitida' ? 'positivo' : x.situacao === 'Cancelada' ? 'encerrado' : 'backlog'} />
                    </td>
                    <td className="numero nowrap">
                      {valor}
                      <div className="texto-secundario">{c.locacao ? 'total do contrato' : 'valor ao Cliente'}</div>
                    </td>
                    {!tradein && <td>—</td>}
                    <td>—</td>
                    <td>{x.situacao === 'Cancelada' ? 'Cancelada' : '—'}</td>
                    <td className="col-acoes nowrap">
                      <a className="botao-secundario pequeno" href={`#/ordens/${x.numero}`}>
                        Abrir
                      </a>{' '}
                      <a className="botao-secundario pequeno" href={`#/ordens/${x.numero}/pdf`}>
                        PDF
                      </a>
                    </td>
                  </tr>
                );
              })}
              {tradein && total === 0 && (
                <tr>
                  <td colSpan={10} className="vazio-tabela" data-teste="ordens-vazio">
                    Nenhuma OTI gerada.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </RolagemIndicada>
      )}
      {podeGerarOti && (
        <div className="acoes-form acoes-gerar-ocf acoes-gerar-oti">
          {!serieOk && (
            <p className="aviso aviso-erro sem-margem" role="alert" data-teste="serie-oti-indisponivel">
              <strong>Geração indisponível:</strong> série OTI não configurada neste ambiente.
            </p>
          )}
          <button type="button" className="botao-primario" disabled={!serieOk} onClick={() => setConferirOti(true)} data-teste="abrir-conferencia-oti">
            Gerar OTI
          </button>
        </div>
      )}
      {conferirOti && <ModalConferirOti pedido={p} aoFechar={() => setConferirOti(false)} />}
      {podeGerarOcf && (
        <div className="acoes-form acoes-gerar-ocf">
          <button type="button" className="botao-primario" onClick={() => setConferir(true)} data-teste="abrir-conferencia-ocf">
            {p.previaOrdens.length > 1 ? `Gerar OCF (${p.previaOrdens.length})` : 'Gerar OCF'}
          </button>
        </div>
      )}
      {oc && <ModalOcCliente pedido={p} foco={oc.foco} aoFechar={() => setOc(null)} aoMudar={aoMudar} />}
      {conferir && (
        <ModalConferirOcf
          pedido={p}
          aoFechar={() => setConferir(false)}
          aoGerar={() => {
            setConferir(false);
            recarregar();
          }}
        />
      )}
    </section>
  );
}

export function TelaPedido({ numero }: { numero: string }) {
  const { pode } = useSessao();
  const carga = useCarga(() => api.get<PedidoCompleto>(`/api/pedidos/${numero}`), [numero]);
  const [modal, setModal] = useState<'' | 'correcao' | 'cancelar' | 'reabertura' | 'historico'>('');
  useRotuloTrilha(carga.dados ? carga.dados.numero : null);
  if (!carga.dados) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="pedido" lixeira={carga.lixeira} />;
  const p = carga.dados;
  const c = p.conteudo;
  const semFornecedor = p.tipo === 'venda' ? c.itens.filter((i) => !i.entidadeId) : [];
  return (
    <>
      <section className="resumo-destaque cabecalho-pedido-compacto" data-teste="cabecalho-pedido">
        <div className="cabecalho-registro">
          <h1 className="titulo-pagina">
            {p.numero}
            {(p.revisao ?? 0) > 0 && (
              <span className="revisao-titulo" data-teste="revisao-pedido">
                {' '}
                revisão r{p.revisao}
              </span>
            )}
          </h1>
          <div className="selos-cabecalho">
            <SeloTipo tipo={p.tipo} />
            <span title={p.tipo === 'venda' ? 'Calculada pelo faturamento das Ordens válidas' : 'Situação ilustrativa até os documentos operacionais'} data-teste="situacao-pedido">
              <SeloSituacao texto={p.situacao} tom={tomPedido(p.situacao)} />
            </span>
            {(p.pendenciasAbertas ?? 0) > 0 && (
              <span className="selo-previa pend" data-teste="selo-pendencias-externas">
                {p.pendenciasAbertas} pendência(s) externa(s) aberta(s)
              </span>
            )}
            {!p.cancelamento && pode('reabrir_correcao', 'pedido') && (
              <button type="button" className="botao-primario" onClick={() => setModal('correcao')} data-teste="reabrir-correcao">
                Reabrir para correção
              </button>
            )}
            {!p.cancelamento && pode('reabrir_comercial', 'oportunidade') && (
              <button type="button" className="botao-secundario" onClick={() => setModal('reabertura')} data-teste="reabertura-comercial">
                Reabertura comercial
              </button>
            )}
            {!p.cancelamento && pode('cancelar', 'pedido') && p.ordens.every((o) => o.estado === 'cancelada') && (
              <button type="button" className="botao-secundario" onClick={() => setModal('cancelar')} data-teste="cancelar-pedido">
                Cancelar Pedido
              </button>
            )}
            <BotaoExcluir entidade="pedido" id={p.id} aoExcluir={depoisDeExcluir('/pedidos')} />
          </div>
        </div>
        <div className="grade-cabecalho grade-cabecalho-pedido grade-cabecalho-compacta" data-teste="grade-cabecalho-pedido">
          <div>
            <span className="rotulo-campo">Cliente</span>
            <a href={`#/clientes/${p.cliente.id}`}>{p.cliente.nome}</a>
            <span className="info-secundaria">{p.clienteCnpj}</span>
          </div>
          <div className="grade-cabecalho-largo" data-teste="origem-pedido">
            <span className="rotulo-campo">Origem</span>
            <a href={`#/oportunidades/${p.oportunidade.id}`}>
              Oportunidade #{p.oportunidade.numero} - {p.oportunidade.titulo}
            </a>
            <a href={`#/oportunidades/${p.oportunidade.id}/propostas/${p.proposta.id}/v/${p.versao}`}>
              Proposta {p.proposta.numero} - v{p.versao}
            </a>
          </div>
          <div>
            <span className="rotulo-campo">Responsável</span>
            {p.oportunidade.responsavel}
          </div>
          <div>
            <span className="rotulo-campo">Gerado em</span>
            {dataBr(p.geradoEm.slice(0, 10))}
          </div>
          <div className="celula-historico">
            <button type="button" className="botao-link botao-historico" onClick={() => setModal('historico')} data-teste="historico-pedido">
              <Icone nome="relogio" /> Histórico
            </button>
          </div>
        </div>
      </section>
      {p.cancelamento && (
        <p className="aviso aviso-alerta" data-teste="pedido-cancelado">
          <strong>Pedido cancelado em {dataBr(p.cancelamento.em.slice(0, 10))}.</strong> Motivo: {p.cancelamento.motivo}
          {p.cancelamento.observacao ? ` — ${p.cancelamento.observacao}` : ''}
        </p>
      )}
      {modal === 'historico' && <ModalHistoricoPedido pedido={p} aoFechar={() => setModal('')} />}
      {modal === 'correcao' && <ModalReabrirCorrecao numero={p.numero} aoFechar={() => setModal('')} aoAplicar={() => void carga.recarregar()} />}
      {modal === 'cancelar' && (
        <ModalCancelarPedido
          numero={p.numero}
          aoFechar={() => setModal('')}
          aoCancelar={() => {
            setModal('');
            void carga.recarregar();
          }}
        />
      )}
      {modal === 'reabertura' && (
        <ModalReaberturaComercial
          oportunidadeId={p.oportunidade.id}
          aoFechar={() => setModal('')}
          aoReabrir={() => {
            setModal('');
            navegar(`/oportunidades/${p.oportunidade.id}`);
          }}
        />
      )}
      <ResumoPedido pedido={p} />
      <FaturamentoEntregaPedido pedido={p} />
      {/* Trade-in: os detalhes ficam dentro do Resumo (referência aprovada, arquivo 04) */}
      {!c.tradein && <DetalhesFinanceiros pedido={p} />}
      <ItensGanhos pedido={p} />
      {c.recebimentoParceiros && Object.keys(c.recebimentoParceiros).length > 0 && (
        <details className="cartao" data-teste="recebimento-comissao-pedido">
          <summary>
            <strong>Recebimento da comissão</strong> <span className="texto-secundario">(copiado no envio da Proposta; uso interno)</span>
          </summary>
          <RegrasRecebimentoDoPedido regras={c.recebimentoParceiros} />
        </details>
      )}
      {semFornecedor.length > 0 && (
        <p className="aviso aviso-alerta" data-teste="sem-fornecedor">
          Sem parceiro faturador selecionado (não gera Ordem): {semFornecedor.map((i) => i.produtoCopia?.nome ?? i.itemId).join(', ')}.
        </p>
      )}
      <RetificacoesDoPedido retificacoes={p.retificacoes ?? []} aoMudar={() => void carga.recarregar()} />
      <OrdensDoPedido pedido={p} aoMudar={(novo) => carga.setDados(novo)} recarregar={() => void carga.recarregar()} />
    </>
  );
}
